import { useState, useEffect, useCallback, useRef, useMemo, Fragment } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Document, Page } from 'react-pdf';
import * as pdfjs from 'pdfjs-dist';
import LazyPdfPage from '../components/LazyPdfPage';
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '../components/ui/resizable';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { ReparseTypeItems, useReparseConfirm, reparseToast } from '../components/reparse/ReparseOptions';
import { Badge } from '../components/ui/badge';
import api from '../lib/api';
import { usePrivacy } from '../lib/privacy';
import { getAuth, isSuperAdmin } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, FileText, Loader2, Save, Edit, X, Plus, RefreshCw, ZoomIn, ZoomOut, Check, AlertTriangle, AlertCircle, History, UserCheck, Lock, Download, ChevronDown, RotateCw, RotateCcw, ChevronRight, Clock } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { DemandSummaryCard } from '../components/DemandSummaryCard';
import { BillingBasisBadge } from '../components/BillingBasisBadge';
import EmissionsCard from '../components/EmissionsCard';
import { StandardizedUsageCard } from '../components/StandardizedUsageCard';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { format } from 'date-fns';

// Use CDN worker that matches the bundled pdfjs version
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// Map a raw/parser commodity string (e.g. "electric", "kWh", "natural gas") to one
// of the canonical meter-table Select options, so the value always displays instead
// of showing blank when the casing/wording differs from the option list.
const COMMODITY_OPTIONS = ['Electricity', 'Gas', 'Water', 'Sewer', 'Waste', 'Internet', 'Heating Oil', 'Other'];
const normalizeCommodity = (raw) => {
  if (!raw) return 'Electricity';
  const s = String(raw).trim().toLowerCase();
  const exact = COMMODITY_OPTIONS.find((o) => o.toLowerCase() === s);
  if (exact) return exact;
  if (/(elec|kwh|\bkw\b|kva|power)/.test(s)) return 'Electricity';
  if (/(gas|therm|ccf|mcf|natural)/.test(s)) return 'Gas';
  if (/(water|gallon|\bhcf\b|\bkgal\b)/.test(s)) return 'Water';
  if (/(sewer|wastewater|storm)/.test(s)) return 'Sewer';
  if (/(waste|trash|refuse|garbage|recycl)/.test(s)) return 'Waste';
  if (/(internet|broadband|telecom|cable|data)/.test(s)) return 'Internet';
  if (/(oil|propane|fuel|diesel)/.test(s)) return 'Heating Oil';
  return 'Other';
};

// Confidence indicator component
const ConfidenceIndicator = ({ confidence, field }) => {
  if (!confidence) return null;
  
  const { level, reason } = confidence;
  
  const icons = {
    high: <Check className="h-4 w-4 text-green-600" />,
    medium: <AlertTriangle className="h-4 w-4 text-yellow-600" />,
    low: <AlertCircle className="h-4 w-4 text-red-600" />
  };
  
  const colors = {
    high: 'border-green-200 bg-green-50',
    medium: 'border-yellow-200 bg-yellow-50',
    low: 'border-red-200 bg-red-50'
  };
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className={`inline-flex items-center justify-center w-6 h-6 rounded ${colors[level]} border cursor-help`}>
            {icons[level]}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          <p className="text-xs font-medium capitalize">{level} Confidence</p>
          <p className="text-xs text-muted-foreground">{reason}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

// Usage Cost Breakdown Accordion Component
const UsageCostBreakdown = ({ usageBreakdown, otherCharges, validationData }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  // Combine items from _usage_breakdown
  const allBreakdownItems = useMemo(() => {
    const items = [];
    
    // Add pre-segregated energy charges from _usage_breakdown
    if (usageBreakdown && usageBreakdown.length > 0) {
      usageBreakdown.forEach(item => {
        items.push({
          description: item.description || item.line_item_description || 'Charge',
          charge: parseFloat(item.amount || item.charge || 0),
          category: item.category || 'Energy Charges'
        });
      });
    }
    
    return items;
  }, [usageBreakdown]);
  
  // Group by category
  const groupedItems = useMemo(() => {
    const groups = {};
    allBreakdownItems.forEach(item => {
      const category = item.category || 'Other';
      if (!groups[category]) {
        groups[category] = { items: [], total: 0 };
      }
      groups[category].items.push(item);
      groups[category].total += item.charge;
    });
    return groups;
  }, [allBreakdownItems]);
  
  const totalBreakdown = allBreakdownItems.reduce((sum, item) => sum + item.charge, 0);
  
  if (allBreakdownItems.length === 0) return null;
  
  return (
    <div className="border border-border border-t-0">
      {/* Collapsed Header */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 transition-colors"
        data-testid="usage-cost-breakdown-toggle"
      >
        <div className="flex items-center gap-2">
          <ChevronRight className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
          <span className="text-sm font-medium">Usage Cost Breakdown</span>
          <Badge variant="secondary" className="rounded-none text-xs">
            {allBreakdownItems.length} items
          </Badge>
        </div>
        <span className="text-sm font-mono font-medium">${totalBreakdown.toFixed(2)}</span>
      </button>
      
      {/* Expanded Content */}
      {isOpen && (
        <div className="p-4 bg-white space-y-4" data-testid="usage-cost-breakdown-content">
          {Object.entries(groupedItems).map(([category, group]) => (
            <div key={category} className="space-y-2">
              <div className="flex items-center justify-between text-sm font-semibold text-slate-700 border-b pb-1">
                <span>{category}</span>
                <span className="font-mono">${group.total.toFixed(2)}</span>
              </div>
              <div className="space-y-1 pl-4">
                {group.items.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between text-xs text-slate-600">
                    <span className="truncate max-w-[280px]" title={item.description}>
                      {item.description}
                    </span>
                    <span className="font-mono">${item.charge.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
          
          {/* Validation Fixes Applied */}
          {validationData?.fixes_applied && validationData.fixes_applied.length > 0 && (
            <div className="border-t pt-3 mt-3">
              <p className="text-xs font-medium text-slate-600 mb-2">Auto-corrections Applied:</p>
              <div className="space-y-1">
                {validationData.fixes_applied.map((fix, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-xs text-green-700">
                    <Check className="h-3 w-3" />
                    <span>{fix}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Summary Footer */}
          <div className="border-t pt-2 mt-3">
            <div className="flex items-center justify-between text-sm font-bold">
              <span>Total Breakdown</span>
              <span className="font-mono">${totalBreakdown.toFixed(2)}</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              These energy charges were segregated from other charges for validation.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export const Viewer = () => {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const { confirm: confirmReparse, dialog: reparseConfirmDialog } = useReparseConfirm();
  const privacy = usePrivacy();
  const currentAccountStr = localStorage.getItem('current_account');
  const currentAccount = currentAccountStr ? JSON.parse(currentAccountStr) : null;
  
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [numPages, setNumPages] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reparsing, setReparsing] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfLoadError, setPdfLoadError] = useState(null);
  const [hasJpeg2000Warning, setHasJpeg2000Warning] = useState(false);
  
  // PDF Zoom, Search, Navigation and Rotation
  const [scale, setScale] = useState(1.0);
  const [rotation, setRotation] = useState(0);  // NEW: PDF rotation (0, 90, 180, 270)
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const pdfContainerRef = useRef(null);
  
  // Confidence scores
  const [confidenceScores, setConfidenceScores] = useState({});
  const [userEditedFields, setUserEditedFields] = useState([]);
  
  // Document workflow
  const [reviewStatus, setReviewStatus] = useState('review');
  const [assignedTo, setAssignedTo] = useState(null);
  const [accountHasProperties, setAccountHasProperties] = useState(false);
  const [accountUsers, setAccountUsers] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [showAuditModal, setShowAuditModal] = useState(false);
  
  // BillPortal export
  const [hasBillPortalExport, setHasBillPortalExport] = useState(false);
  
  // Row editing state
  const [editingRowId, setEditingRowId] = useState(null); // e.g., 'meter-0', 'usage-2'
  const [reviewedRows, setReviewedRows] = useState(new Set()); // Rows marked as worked/checked
  const [showValidationWarnings, setShowValidationWarnings] = useState(false);
  
  // Statement data - Unified schema for both single and consolidated bills
  const [statement, setStatement] = useState({
    // Property/Location Info
    property_name: '',
    billing_address: '',  // NEW: Billing address (replaces service_address at statement level)
    service_address: '',  // kept internally for backward-compat / downstream mapping
    city: '',
    state: '',
    postcode: '',
    country: '',
    // Customer Info
    customer_name: '',
    account_no: '',
    meter_number: '',  // kept internally (hidden in UI) for downstream mapping
    // Vendor Info
    vendor_name: '',
    commodity: 'Electricity',  // kept internally (hidden in UI) for downstream mapping
    // Invoice Info
    invoice_no: '',
    issue_date: '',
    due_date: '',
    period_from: '',
    period_to: '',
    // Financial Info
    previous_balance: '',
    payments_received: '',
    balance_forward: '',
    late_fee: '',      // NEW: Late fee at statement level
    credits: '',       // NEW: Credits applied
    adjustments: '',   // NEW: Adjustments
    gst: '',           // NEW: GST — extracted directly by AI at statement level (no auto-derive from provider_charges)
    new_charges: '',
    total_amount_due: ''
  });

  // Invoice-No sanitizer: block alphabet characters (allow digits + common
  // separators like - _ / . and spaces which real invoice IDs often contain).
  const isValidInvoiceNo = (val) => val === '' || !/[A-Za-z]/.test(String(val));
  const handleInvoiceNoChange = (e) => {
    const v = e.target.value;
    if (isValidInvoiceNo(v)) {
      setStatement((prev) => ({ ...prev, invoice_no: v }));
    }
  };

  // Numeric-only sanitizer: allow empty, digits, single decimal, optional leading minus
  const isValidAmount = (val) => val === '' || /^-?\d*\.?\d*$/.test(String(val));
  const handleAmountChange = (field) => (e) => {
    const v = e.target.value;
    if (isValidAmount(v)) {
      setStatement((prev) => ({ ...prev, [field]: v }));
    }
  };

  // Due date must be on or after Issue date
  const dueDateError = useMemo(() => {
    const iss = statement.issue_date;
    const due = statement.due_date;
    if (iss && due && due < iss) {
      return 'Due date cannot be earlier than issue date';
    }
    return '';
  }, [statement.issue_date, statement.due_date]);
  
  // NEW: Validation state from AI extraction
  const [validationData, setValidationData] = useState(null);
  
  const [providerCharges, setProviderCharges] = useState([]);
  const [meterReads, setMeterReads] = useState([]);
  const [usageDetails, setUsageDetails] = useState([]);
  const [otherCharges, setOtherCharges] = useState([]);
  const [usageBreakdown, setUsageBreakdown] = useState([]); // NEW: Energy charges breakdown
  const [usageSummary, setUsageSummary] = useState(null); // Demand/Power-Quality summary

  // Balance validation logic - Updated per user requirements
  const balanceValidation = useMemo(() => {
    const parseAmount = (val) => {
      if (!val || val === '' || val === 'None' || val === 'N/A') return 0;
      const cleaned = String(val).replace(/[$,]/g, '').trim();
      return parseFloat(cleaned) || 0;
    };

    const previousBalance = parseAmount(statement.previous_balance);
    const paymentsReceived = parseAmount(statement.payments_received);
    const balanceForward = parseAmount(statement.balance_forward);
    const newCharges = parseAmount(statement.new_charges);
    const totalAmountDue = parseAmount(statement.total_amount_due);

    // Updated BBF Logic:
    // If Previous Balance = Payments Received, then BBF can be zero (valid)
    // If Previous Balance != Payments Received, BBF = Previous Balance - Payments Received
    const expectedBalanceForward = previousBalance - paymentsReceived;
    
    let isValid = false;
    let message = '';

    // Rule 1: No previous balance data at all - skip validation
    if (previousBalance === 0 && paymentsReceived === 0 && balanceForward === 0) {
      isValid = true;
      message = 'No outstanding balance';
    }
    // Rule 2: Previous Balance equals Payments Received - BBF should be 0
    else if (Math.abs(previousBalance - paymentsReceived) < 0.01) {
      if (balanceForward === 0) {
        isValid = true;
        message = 'Previous balance paid in full (BBF = 0)';
      } else {
        isValid = false;
        message = `BBF should be 0 when Previous Balance = Payments Received`;
      }
    }
    // Rule 3: Previous Balance != Payments Received - BBF = Previous - Payments
    else if (Math.abs(expectedBalanceForward - balanceForward) < 0.01) {
      isValid = true;
      if (balanceForward > 0) {
        message = `BBF verified: $${balanceForward.toFixed(2)} (Previous - Payments)`;
      } else {
        message = 'Balance calculation verified';
      }
    }
    // Rule 4: If Balance Forward is 0 and New Charges equals Total Amount Due - valid scenario
    else if (balanceForward === 0 && newCharges > 0 && Math.abs(newCharges - totalAmountDue) < 0.01) {
      isValid = true;
      message = 'New charges equal total due (no carry-over balance)';
    }
    // Rule 5: Check if total adds up correctly
    else if (Math.abs((balanceForward + newCharges) - totalAmountDue) < 0.01) {
      isValid = true;
      message = 'Total amount due calculation verified';
    }
    else {
      isValid = false;
      message = `Expected BBF: $${expectedBalanceForward.toFixed(2)}, Actual: $${balanceForward.toFixed(2)}`;
    }

    return { isValid, message, previousBalance, paymentsReceived, balanceForward, expectedBalanceForward };
  }, [statement.previous_balance, statement.payments_received, statement.balance_forward, statement.new_charges, statement.total_amount_due]);

  // Meter read date validation
  const meterValidation = useMemo(() => {
    const validations = meterReads.map((read, index) => {
      const prevDate = read.previous_read_date;
      const currDate = read.current_read_date;
      
      if (!prevDate || !currDate) {
        return { index, isValid: true, message: '' }; // Skip if dates missing
      }
      
      const prevDateObj = new Date(prevDate);
      const currDateObj = new Date(currDate);
      
      if (isNaN(prevDateObj.getTime()) || isNaN(currDateObj.getTime())) {
        return { index, isValid: true, message: '' }; // Skip invalid dates
      }
      
      if (prevDateObj >= currDateObj) {
        return {
          index,
          isValid: false,
          message: `Meter ${read.meter_number || index + 1}: Previous read date (${prevDate}) must be before current read date (${currDate})`
        };
      }
      
      return { index, isValid: true, message: '' };
    });
    
    const hasErrors = validations.some(v => !v.isValid);
    const errorMessages = validations.filter(v => !v.isValid).map(v => v.message);
    
    return { validations, hasErrors, errorMessages };
  }, [meterReads]);

  // Usage line item validation (usage × rate = charge)
  const usageValidation = useMemo(() => {
    const parseNum = (val) => {
      if (!val || val === '' || val === 'None' || val === '-') return null;
      const cleaned = String(val).replace(/[$,]/g, '').trim();
      const num = parseFloat(cleaned);
      return isNaN(num) ? null : num;
    };

    const validations = usageDetails.map((item, index) => {
      const usage = parseNum(item.usage_quantity);
      const rate = parseNum(item.rate);
      const charge = parseNum(item.charge);
      
      // Skip validation if any required field is missing
      if (usage === null || rate === null || charge === null) {
        return { index, isValid: true, message: '', calculated: null };
      }
      
      const calculated = usage * rate;
      const difference = Math.abs(calculated - charge);
      const tolerance = Math.max(0.02, Math.abs(charge * 0.01)); // 1% or $0.02 tolerance
      
      if (difference > tolerance) {
        return {
          index,
          isValid: false,
          message: `Row ${index + 1}: ${usage} × ${rate} = ${calculated.toFixed(2)}, but charge shows ${charge.toFixed(2)}`,
          calculated
        };
      }
      
      return { index, isValid: true, message: '', calculated };
    });
    
    const hasErrors = validations.some(v => !v.isValid);
    const errorMessages = validations.filter(v => !v.isValid).map(v => v.message);
    
    return { validations, hasErrors, errorMessages };
  }, [usageDetails]);

  // UOM validation based on commodity
  const uomValidation = useMemo(() => {
    const expectedUOM = {
      'Electricity': ['kWh', 'kwh', 'KWH', 'MWh', 'mwh'],
      'Gas': ['Therms', 'therms', 'THERMS', 'CCF', 'ccf', 'MCF', 'Dth', 'DTH'],
      'Water': ['Gallons', 'gallons', 'GAL', 'gal', 'HCF', 'hcf', 'CCF', 'ccf', 'kGal', 'KGAL'],
      'Sewer': ['Gallons', 'gallons', 'GAL', 'gal', 'HCF', 'hcf', 'CCF', 'ccf']
    };

    const validations = usageDetails.map((item, index) => {
      const commodity = item.commodity || statement.commodity;
      const uom = item.uom;
      
      if (!commodity || !uom) {
        return { index, isValid: true, message: '' };
      }
      
      const expected = expectedUOM[commodity] || [];
      if (expected.length === 0) {
        return { index, isValid: true, message: '' };
      }
      
      const isValidUOM = expected.some(e => uom.toLowerCase().includes(e.toLowerCase()));
      
      if (!isValidUOM) {
        return {
          index,
          isValid: false,
          message: `Row ${index + 1}: UOM "${uom}" unexpected for ${commodity}. Expected: ${expected.slice(0, 3).join(', ')}`
        };
      }
      
      return { index, isValid: true, message: '' };
    });
    
    const hasErrors = validations.some(v => !v.isValid);
    const errorMessages = validations.filter(v => !v.isValid).map(v => v.message);
    
    return { validations, hasErrors, errorMessages };
  }, [usageDetails, statement.commodity]);

  // Mandatory statement-level fields — consolidated-style logic:
  // Property Name is NOT mandatory (auto-filled from onboarding / service
  // address). Period From/To are only mandatory for metered utility
  // commodities (Electricity, Gas, Water); optional for all others.
  // Reported inline (next to each label) and in the Validation tab.
  const MANDATORY_STATEMENT_FIELDS = useMemo(() => {
    const periodRequired = ['electricity', 'gas', 'water'].includes(
      String(statement.commodity || '').trim().toLowerCase()
    );
    return [
      { key: 'vendor_name', label: 'Vendor Name' },
      { key: 'account_no', label: 'Account No' },
      { key: 'customer_name', label: 'Org Name / Bill To Name' },
      { key: 'issue_date', label: 'Issue Date' },
      ...(periodRequired
        ? [{ key: 'period_from', label: 'Period From' }, { key: 'period_to', label: 'Period To' }]
        : []),
      { key: 'new_charges', label: 'New Charges' },
      { key: 'total_amount_due', label: 'Total Amount Due' },
    ];
  }, [statement.commodity]);
  const isBlankField = useCallback((v) => {
    if (v === null || v === undefined) return true;
    const s = String(v).trim();
    return s === '' || s === 'N/A' || s === 'None' || s === 'null';
  }, []);
  const mandatoryFieldsValidation = useMemo(() => {
    const missing = MANDATORY_STATEMENT_FIELDS.filter((f) => isBlankField(statement[f.key]));
    return {
      total: MANDATORY_STATEMENT_FIELDS.length,
      missing,
      allPresent: missing.length === 0,
      missingKeys: new Set(missing.map((f) => f.key)),
    };
  }, [MANDATORY_STATEMENT_FIELDS, isBlankField,
    statement.property_name, statement.vendor_name, statement.account_no,
    statement.customer_name, statement.issue_date, statement.period_from,
    statement.period_to, statement.new_charges, statement.total_amount_due
  ]);
  // NEW: Comprehensive Bill Validation - Usage + Other Charges = New Charges, Total Amount Due verification
  const billValidation = useMemo(() => {
    const parseAmount = (val) => {
      if (!val || val === '' || val === 'None' || val === 'N/A') return 0;
      const cleaned = String(val).replace(/[$,]/g, '').trim();
      return parseFloat(cleaned) || 0;
    };

    // Calculate totals (line-item charge field varies: charges | charge | line_item_charge)
    const usageTotal = usageDetails.reduce((sum, item) => sum + parseAmount(item.charges ?? item.charge ?? item.line_item_charge), 0);
    const otherChargesTotal = otherCharges.reduce((sum, item) => sum + parseAmount(item.charge ?? item.charges ?? item.line_item_charge), 0);
    const lineItemsSum = usageTotal + otherChargesTotal;
    
    // Statement values
    const newCharges = parseAmount(statement.new_charges);
    const totalAmountDue = parseAmount(statement.total_amount_due);
    const lateFee = parseAmount(statement.late_fee);
    const credits = parseAmount(statement.credits);
    const adjustments = parseAmount(statement.adjustments);
    const balanceForward = parseAmount(statement.balance_forward);

    // Late fee may be itemized in Other Charges or carried only in the scalar field.
    const lateFeeInOther = otherCharges.some(item => {
      const desc = (item.line_item_description || '').toLowerCase();
      return desc.includes('late') && Math.abs(parseAmount(item.charge) - lateFee) < 0.01;
    });
    const effLate = lateFeeInOther ? 0 : lateFee;

    // Validation checks — allow the un-itemized late fee to close a line-item gap.
    const lineItemsMatchNewCharges =
      Math.abs(lineItemsSum - newCharges) < 0.01 ||
      Math.abs((lineItemsSum + effLate) - newCharges) < 0.01;

    // Total Due: accept the late fee whether it sits INSIDE New Charges (new + bbf)
    // or is added on top (new + late + bbf).
    const calculatedTotalDue = newCharges + effLate - credits + adjustments + balanceForward;
    const totalDueMatches =
      Math.abs(calculatedTotalDue - totalAmountDue) < 0.01 ||
      Math.abs((newCharges - credits + adjustments + balanceForward) - totalAmountDue) < 0.01 ||
      Math.abs(newCharges - totalAmountDue) < 0.01;
    
    // Calculate validation percentage
    let validationScore = 0;
    if (lineItemsMatchNewCharges) validationScore += 50;
    if (totalDueMatches) validationScore += 50;
    
    const isFullyValid = lineItemsMatchNewCharges && totalDueMatches;
    
    return {
      usageTotal,
      otherChargesTotal,
      lineItemsSum,
      newCharges,
      lineItemsMatchNewCharges,
      totalAmountDue,
      calculatedTotalDue,
      totalDueMatches,
      validationScore,
      isFullyValid,
      breakdown: {
        lateFee,
        credits,
        adjustments,
        balanceForward
      }
    };
  }, [usageDetails, otherCharges, statement.new_charges, statement.total_amount_due, statement.late_fee, statement.credits, statement.adjustments, statement.balance_forward]);

  // Intelligent Validation (Engine-2) - All line items from Usage + Other Charges
  const intelligentValidation = useMemo(() => {
    const parseAmount = (val) => {
      if (!val || val === '' || val === 'None' || val === 'N/A') return 0;
      const cleaned = String(val).replace(/[$,]/g, '').trim();
      return parseFloat(cleaned) || 0;
    };

    const newCharges = parseAmount(statement.new_charges);
    const totalAmountDue = parseAmount(statement.total_amount_due);

    // Penny-tolerant equality — absorbs multi-line rounding drift (~0.01*N) + floats.
    const centsMatch = (a, b, n = 1) => Math.abs(Math.round(a * 100) - Math.round(b * 100)) <= Math.max(1, n) + 1e-9;

    // A "Late payment charge if paid after <date>" line is a conditional future
    // penalty preview (utilities print 1-2 alternative amounts) — not a current
    // charge. Parser-flagged duplicate totals / summary rows are also excluded.
    const isConditionalPenalty = (desc) => {
      const d = String(desc || '').toLowerCase();
      return d.includes('if paid after') || d.includes('if paid by') ||
             d.includes('if not paid') || (d.includes('late') && d.includes('if paid'));
    };
    const isExcluded = (item, desc) => Boolean(item._duplicate_total || item.is_total) || isConditionalPenalty(desc);
    
    // Collect ALL line items from both Usage Details and Other Charges
    const validationLineItems = [];
    
    // Add all usage details items
    usageDetails.forEach((item, idx) => {
      const charge = parseAmount(item.charges || item.charge);
      const description = item.line_item_description || item.description || 'Usage Charge';
      validationLineItems.push({
        ...item,
        source: 'Usage Details',
        originalIndex: idx,
        description,
        charge,
        _excluded: isExcluded(item, description),
        page_number: item.page_number
      });
    });
    
    // Add all other charges items
    otherCharges.forEach((item, idx) => {
      const charge = parseAmount(item.charge);
      const description = item.line_item_description || 'Other Charge';
      validationLineItems.push({
        ...item,
        source: 'Other Charges',
        originalIndex: idx,
        description,
        charge,
        _excluded: isExcluded(item, description),
        page_number: item.page_number
      });
    });
    
    // Calculate total of all line items (excluded summary/penalty rows omitted)
    const fullValidationTotal = validationLineItems.reduce(
      (sum, item) => sum + (item._excluded ? 0 : item.charge), 0);
    const fullItemCount = validationLineItems.filter(i => !i._excluded).length;
    // Lines tagged `_outside_new_charges` (e.g. a PECO Connection Charge billed
    // in a separate "Other Charges" bucket) are real and count toward Total
    // Amount Due, but are NOT part of "New Charges" — exclude them from the
    // New-Charges reconciliation only.
    const validationTotal = validationLineItems.reduce(
      (sum, item) => sum + ((item._outside_new_charges || item._excluded) ? 0 : item.charge), 0);
    const valItemCount = validationLineItems.filter(i => !i._outside_new_charges && !i._excluded).length;
    const outsideNewChargesTotal = fullValidationTotal - validationTotal;
    
    // NEW CHARGES VALIDATION inputs — the late fee is needed here too (see below).
    const lateFee = parseAmount(statement.late_fee);
    const credits = parseAmount(statement.credits);
    const adjustments = parseAmount(statement.adjustments);
    const balanceForward = parseAmount(statement.balance_forward);

    // Check if late fee is already included in Other Charges
    const lateFeeInOtherCharges = otherCharges.some(item => {
      const desc = (item.line_item_description || '').toLowerCase();
      return desc.includes('late') && Math.abs(parseAmount(item.charge) - lateFee) < 0.01;
    });

    // Method 1: Standard formula (New Charges + Balance Forward + Late Fee - Credits)
    // Only add late fee if it's NOT already in other charges
    const effectiveLateFee = lateFeeInOtherCharges ? 0 : lateFee;

    // NEW CHARGES VALIDATION: itemized line items (optionally + the un-itemized late
    // fee) should add up to New Charges. A real Late Payment Charge is part of New
    // Charges but is often carried only in the scalar `late_fee` field, so allow it
    // to close a gap that would otherwise equal exactly the late-fee amount. Likewise,
    // when the itemised lines INCLUDE tax but New Charges is the pre-tax subtotal,
    // allow (line-item sum − tax) to match New Charges.
    const isTaxLine = (desc) => /\btax(?:es)?\b/i.test(String(desc || ''));
    const taxFromLines = validationLineItems.reduce(
      (sum, item) => sum + ((!item._excluded && !item._outside_new_charges && isTaxLine(item.description)) ? item.charge : 0), 0);
    const validationMatchesNewCharges =
      centsMatch(validationTotal, newCharges, valItemCount) ||
      centsMatch(validationTotal + effectiveLateFee, newCharges, valItemCount) ||
      centsMatch(validationTotal - taxFromLines, newCharges, valItemCount) ||
      // Balance listings itemise the TOTAL balance by service (current + past due).
      (statement.line_items_basis === 'total_balance' &&
        centsMatch(fullValidationTotal, totalAmountDue, fullItemCount) &&
        centsMatch(newCharges + balanceForward, totalAmountDue, 2));
    const newChargesDifference = validationTotal - newCharges;
    const calculatedTotalDue = newCharges + balanceForward + effectiveLateFee - credits + adjustments;
    const totalDueMatchesMethod1 = centsMatch(calculatedTotalDue, totalAmountDue, 2);
    
    // Method 2: If New Charges == Total Due (common for bills with no balance forward)
    const totalDueMatchesMethod2 = centsMatch(newCharges, totalAmountDue, 1);
    
    // Method 3: All Line Items + Balance Forward = Total Due (late fee already in line items)
    // Uses the FULL total (incl. any 'outside New Charges' lines, which ARE in Total Due).
    const allItemsPlusBalance = fullValidationTotal + balanceForward - credits + adjustments;
    const totalDueMatchesMethod3 = centsMatch(allItemsPlusBalance, totalAmountDue, fullItemCount + 1);
    
    // Method 4: New Charges + Balance Forward = Total Due (no late fee adjustment)
    const newChargesPlusBF = newCharges + balanceForward - credits + adjustments;
    const totalDueMatchesMethod4 = centsMatch(newChargesPlusBF, totalAmountDue, 2);

    // Credit-balance floor: an account carrying a credit shows Total Amount Due $0
    // (remaining credit carries forward, "Do Not Pay"). Reconciled both when the
    // standard formula computes a net credit (<= 0) AND when the bill prints an
    // explicit credit balance (credits / balance-forward negative) that the summary
    // box doesn't otherwise reconcile — the CHARGES are still verified above.
    const totalDueMatchesCreditFloor = totalAmountDue <= 0.01 &&
      (calculatedTotalDue <= 0.01 || credits < -0.01 || balanceForward < -0.01);

    // Total Due is valid if ANY method matches
    const totalDueMatches = totalDueMatchesMethod1 || totalDueMatchesMethod2 || totalDueMatchesMethod3 || totalDueMatchesMethod4 || totalDueMatchesCreditFloor;
    const totalDueDifference = totalDueMatchesMethod1 ? calculatedTotalDue - totalAmountDue :
                               totalDueMatchesMethod3 ? allItemsPlusBalance - totalAmountDue :
                               totalDueMatchesMethod4 ? newChargesPlusBF - totalAmountDue :
                               totalDueMatchesCreditFloor ? 0 :
                               newCharges - totalAmountDue;
    
    // Determine validation method used for display
    const totalDueValidationMethod = totalDueMatchesMethod1 ? 'standard' :
                                     totalDueMatchesMethod2 ? 'direct_match' :
                                     totalDueMatchesMethod3 ? 'line_items_plus_balance' :
                                     totalDueMatchesMethod4 ? 'new_charges_plus_bf' :
                                     totalDueMatchesCreditFloor ? 'credit_carryforward' : 'none';
    
    // BOTH VALID: When line items match New Charges AND Total Due can be validated
    const bothValid = validationMatchesNewCharges && totalDueMatches;
    
    // Calculate validation accuracy percentage (for consistency with bill list)
    let validationAccuracy = 0;
    if (validationMatchesNewCharges) validationAccuracy += 50;
    if (totalDueMatches) validationAccuracy += 50;
    
    return {
      enabled: false,
      validationLineItems,
      validationTotal,
      fullValidationTotal,
      outsideNewChargesTotal,
      newCharges,
      validationMatchesNewCharges,
      newChargesDifference,
      balanceBasis: statement.line_items_basis === 'total_balance',
      balanceForward,
      totalAmountDue,
      calculatedTotalDue,
      totalDueMatches,
      totalDueDifference,
      totalDueValidationMethod,
      bothValid,
      validationAccuracy,
      breakdown: { 
        usageCount: usageDetails.length,
        otherCount: otherCharges.length,
        lateFee,
        effectiveLateFee,
        lateFeeInOtherCharges,
        credits, 
        adjustments, 
        balanceForward
      }
    };
  }, [usageDetails, otherCharges, statement]);

  // Approval is gated on financial reconciliation only (New Charges + Total
  // Amount Due). Missing metadata like property name is surfaced in the
  // Validation tab as a warning but does NOT hard-block approval — the parse
  // pipeline auto-approves reconciled bills the same way, so this keeps manual
  // and automatic approval consistent.
  const canApprove = useMemo(() => (
    intelligentValidation.bothValid
  ), [intelligentValidation.bothValid]);

  const approveBlockReasons = useMemo(() => {
    const reasons = [];
    if (!intelligentValidation.validationMatchesNewCharges) reasons.push('New Charges mismatch');
    if (!intelligentValidation.totalDueMatches) reasons.push('Total Amount Due mismatch');
    return reasons;
  }, [intelligentValidation.validationMatchesNewCharges, intelligentValidation.totalDueMatches]);

  useEffect(() => {
    if (!currentAccount) {
      toast.error('No account selected');
      navigate('/bill-jobs');
      return;
    }
    fetchDocument();
    fetchPdfAsBlob();
    fetchAccountUsers();
    fetchAuditLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId, currentAccount?.id, navigate]);

  const fetchPdfAsBlob = async () => {
    if (!currentAccount) return;
    try {
      // First get file info (type and URL)
      const response = await api.get(
        `/accounts/${currentAccount.id}/documents/${documentId}/file`
      );
      
      let blob;
      if (response.data.type === 's3') {
        // Fetch from S3 presigned URL directly
        const s3Response = await fetch(response.data.url);
        blob = await s3Response.blob();
      } else {
        // Local file - use the download endpoint
        const blobResponse = await api.get(
          `/accounts/${currentAccount.id}/documents/${documentId}/file/download`,
          { responseType: 'blob' }
        );
        blob = new Blob([blobResponse.data], { type: 'application/pdf' });
      }
      
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
    } catch (error) {
      console.error('Failed to load PDF:', error);
      toast.error('Failed to load PDF file');
    }
  };

  const fetchDocument = async () => {
    if (!currentAccount) return;
    try {
      const { data } = await api.get(`/accounts/${currentAccount.id}/documents/${documentId}`);
      setDocument(data.document);
      
      // Set document workflow state
      setReviewStatus(data.document.review_status || 'review');
      setAssignedTo(data.document.assigned_to || null);
      
      // Check if BillPortal export exists
      setHasBillPortalExport(data.document.billportal_exported || false);
      
      // Set confidence scores
      if (data.confidence_scores) {
        setConfidenceScores(data.confidence_scores);
      }
      setUserEditedFields(Array.isArray(data.user_edited_fields) ? data.user_edited_fields : []);
      setAccountHasProperties(!!data.account_has_properties);
      
      if (data.extracted_data) {
        const extracted = data.extracted_data;

        // Org / Bill-To name resolution + legacy period fallback.
        // Legacy consolidated-split records store the period in
        // billing_period_start/end and leave customer_name as "N/A" while
        // billed_customer_name holds the real payer — resolve here so the
        // viewer shows real values and mandatory validation stops failing.
        const cleanName = (v) => {
          const s = String(v ?? '').trim();
          return (s === '' || ['n/a', 'none', 'null', 'unknown'].includes(s.toLowerCase())) ? '' : s;
        };
        const resolvedOrgName =
          cleanName(data.document?.linked_location_name) ||
          cleanName(extracted.property_name) ||
          cleanName(extracted.billed_to_org_name) ||
          cleanName(extracted.billed_customer_name) ||
          cleanName(extracted.customer_name);

        setStatement({
          property_name: extracted.property_name || '',
          vendor_name: extracted.vendor_name || '',
          commodity: extracted.commodity || 'Electricity',
          account_no: extracted.account_no || '',
          meter_number: extracted.meter_number || '',  // Hidden in UI, kept for downstream mapping
          customer_name: resolvedOrgName,
          billing_address: extracted.billing_address || extracted.service_address || '',  // NEW: prefer billing_address, fall back to legacy service_address
          service_address: extracted.service_address || '',  // Hidden in UI, kept for downstream mapping
          city: extracted.city || '',
          state: extracted.state || '',
          postcode: extracted.postcode || '',
          country: extracted.country || extracted.service_country || extracted.primary_service_country || '',
          invoice_no: extracted.invoice_no || '',
          issue_date: extracted.issue_date || '',
          period_from: extracted.period_from || extracted.billing_period_start || '',
          period_to: extracted.period_to || extracted.billing_period_end || '',
          due_date: extracted.due_date || '',
          previous_balance: extracted.previous_balance || '',
          payments_received: extracted.payments_received || '',
          balance_forward: extracted.balance_forward || '',
          late_fee: extracted.late_fee || '',         // NEW: Late fee
          credits: extracted.credits || '',           // NEW: Credits
          adjustments: extracted.adjustments || '',   // NEW: Adjustments
          gst: extracted.gst || '',                   // NEW: GST — extracted directly by AI (no fallback)
          new_charges: extracted.new_charges ?? '',
          // Preserve a legitimate $0 (a credit / nothing-to-pay bill has Total
          // Amount Due = 0) — `|| ''` would collapse 0 to blank and wrongly flag
          // Total Amount Due as a MISSING mandatory field. `??` keeps a real 0 while
          // still treating null/absent as missing.
          total_amount_due: extracted.total_amount_due ?? '',
          line_items_basis: extracted.line_items_basis || ''
        });
        
        // NEW: Set validation data if present
        if (extracted._validation) {
          setValidationData(extracted._validation);
        }
        
        // Load provider charges (for split-billing single bills)
        const providerChargesList = Array.isArray(extracted.provider_charges) ? extracted.provider_charges : [];
        setProviderCharges(providerChargesList);
        
        // Normalize usage details to handle both single and consolidated bill formats
        const normalizedUsage = (Array.isArray(extracted.usage_details) ? extracted.usage_details : []).map(item => ({
          ...item,
          // Handle field name differences between single and consolidated bills
          usage: item.usage || item.usage_quantity || '',
          usage_rate: item.usage_rate || item.rate || '',
          charges: item.charges || item.line_item_charge || '',
          commodity: item.commodity || 'Electricity',
          usage_type: item.usage_type || 'Both',
          line_item_description: item.line_item_description || item.rate_period || '',
          is_total: item.is_total || false  // NEW: Flag for summed totals
        }));
        
        setMeterReads(Array.isArray(extracted.meter_reads) ? extracted.meter_reads : []);
        setUsageDetails(normalizedUsage);
        setOtherCharges(Array.isArray(extracted.other_charges) ? extracted.other_charges : []);
        setUsageBreakdown(Array.isArray(extracted._usage_breakdown) ? extracted._usage_breakdown : []);
        setUsageSummary(extracted.usage_summary && extracted.usage_summary.per_meter ? extracted.usage_summary : null);
        
        // Store validation data from AI
        if (extracted._validation) {
          setValidationData(extracted._validation);
        }
      }
    } catch (error) {
      toast.error('Failed to load document');
      navigate('/bill-jobs');
    } finally {
      setLoading(false);
    }
  };

  const fetchAccountUsers = async () => {
    if (!currentAccount) return;
    try {
      const { data } = await api.get(`/accounts/${currentAccount.id}/users-list`);
      setAccountUsers(data);
    } catch (error) {
      console.error('Failed to load users');
    }
  };

  const fetchAuditLogs = async () => {
    if (!currentAccount) return;
    try {
      const { data } = await api.get(`/accounts/${currentAccount.id}/documents/${documentId}/audit`);
      setAuditLogs(data);
    } catch (error) {
      console.error('Failed to load audit logs');
    }
  };

  const handleStatusChange = async (newStatus) => {
    // Block Approved & Locked when the bill has unresolved validation issues.
    if (newStatus === 'approved' && !canApprove) {
      toast.error(
        `Cannot approve — resolve validation issues first: ${approveBlockReasons.join(', ')}. Open the Validation tab for details.`,
        { duration: 6000 }
      );
      return;
    }
    try {
      await api.patch(`/accounts/${currentAccount.id}/documents/${documentId}/status`, {
        review_status: newStatus
      });
      setReviewStatus(newStatus);
      toast.success(`Status changed to ${newStatus}`);
      fetchAuditLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleAssignment = async (userId) => {
    try {
      await api.patch(`/accounts/${currentAccount.id}/documents/${documentId}/assign`, {
        user_id: userId || null
      });
      const assignedUser = accountUsers.find(u => u.id === userId);
      setAssignedTo(userId);
      toast.success(userId ? `Assigned to ${assignedUser?.name}` : 'Assignment removed');
      fetchDocument();
      fetchAuditLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign');
    }
  };

  const handleReparse = (mode = 'all', force = null) =>
    confirmReparse(document, () => runReparse(mode, force));

  const runReparse = async (mode = 'all', force = null) => {
    setReparsing(true);
    try {
      const { data: rp } = await api.post(`/accounts/${currentAccount.id}/documents/${documentId}/reprocess`, {
        mode: mode,  // 'all' or 'missing'
        force_bill_type: force,
      });
      if (rp?.processing_method === 'postprocess') {
        setReparsing(false);
        toast.success(reparseToast(rp), { duration: 7000 });
        if (rp.action === 'split') navigate('/bill-jobs');
        else if (rp.bill_type === 'consolidated') navigate(`/consolidated-viewer/${documentId}`);
        else fetchDocument();
        return;
      }
      if (rp?.held) {
        setReparsing(false);
        toast.error(rp.detail || 'Bill limit reached', {
          duration: 8000,
          description: 'Upgrade a plan or buy bill credits to re-parse this bill.',
        });
        return;
      }
      toast.success(force ? `Re-parsing as ${force} bill with AI. Please wait...` : mode === 'missing' ? 'Re-parsing missing fields. Please wait...' : 'Full re-parsing started. Please wait...');
      
      // Poll for completion
      const checkInterval = setInterval(async () => {
        const { data } = await api.get(`/accounts/${currentAccount.id}/documents/${documentId}`);
        if (data.document.status === 'completed') {
          clearInterval(checkInterval);
          setReparsing(false);
          toast.success('Re-parsing completed!');
          if (force === 'collective') navigate('/bill-jobs');
          else if (data.document.bill_type === 'consolidated') navigate(`/consolidated-viewer/${documentId}`);
          else fetchDocument();
        } else if (data.document.status === 'failed') {
          clearInterval(checkInterval);
          setReparsing(false);
          toast.error('Re-parsing failed');
        }
      }, 3000);
      
      // Timeout after 2 minutes
      setTimeout(() => {
        clearInterval(checkInterval);
        setReparsing(false);
      }, 120000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start re-parsing');
      setReparsing(false);
    }
  };

  const reparseUsed = (document?.reparse_count || 0) >= 1;
  const reparseLocked = reparseUsed && !isSuperAdmin();

  const handleDownloadJSON = async () => {
    if (!currentAccount || !documentId) return;
    const convertUrl = `/accounts/${currentAccount.id}/parser-exports/${documentId}/convert`;
    const fetchUrl = `/accounts/${currentAccount.id}/parser-exports/by-document/${documentId}`;
    try {
      // Always re-convert on download so the returned JSON reflects the
      // latest saved statement (e.g. gst, billing_address, invoice_no edits).
      toast.info('Refreshing BillPortal JSON…');
      await api.post(convertUrl);
      setHasBillPortalExport(true);

      const { data: exportData } = await api.get(fetchUrl);

      if (exportData) {
        const jsonStr = JSON.stringify(exportData, null, 2);
        const blob = new Blob([jsonStr], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = window.document.createElement('a');
        link.href = url;
        link.download = `${privacy.fileName(document?.filename)?.replace('.pdf', '') || 'bill'}_billportal.json`;
        link.click();
        URL.revokeObjectURL(url);
        toast.success('JSON downloaded');
      } else {
        toast.error('Export data not found');
      }
    } catch (error) {
      toast.error('Failed to download JSON');
      console.error(error);
    }
  };

  // Memoize PDF.js options to avoid unnecessary reloads
  const pdfOptions = useMemo(() => ({
    cMapUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/cmaps/`,
    cMapPacked: true,
    standardFontDataUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/standard_fonts/`,
    wasmUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/wasm/`,
  }), []);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  // Navigate to specific page in PDF
  const navigateToPage = useCallback((pageNum) => {
    if (!pageNum || !numPages) return;
    const page = parseInt(pageNum, 10);
    if (page < 1 || page > numPages) return;
    
    setCurrentPage(page);
    
    // Scroll to the page element
    if (pdfContainerRef.current) {
      const pageElement = pdfContainerRef.current.querySelector(`[data-page-number="${page}"]`);
      if (pageElement) {
        pageElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
    
    toast.info(`Navigating to page ${page}`, { duration: 1500 });
  }, [numPages]);

  // Collective-member single bills carry the statement page (page_range.start).
  // Auto-scroll the PDF to that page once, so the viewer opens on this bill's
  // statement rather than page 1 of the combined source PDF.
  const didAutoScrollRef = useRef(false);
  useEffect(() => {
    if (didAutoScrollRef.current || !numPages) return;
    const start = document?.page_range?.start;
    if (start && start > 1) {
      didAutoScrollRef.current = true;
      setTimeout(() => navigateToPage(start), 500);
    }
  }, [numPages, document, navigateToPage]);


  // Handle row focus - highlight row and navigate to page (text highlighting disabled)
  const handleRowFocus = useCallback((rowId, pageNumber) => {
    setEditingRowId(rowId);
    if (pageNumber) {
      navigateToPage(pageNumber);
    }
  }, [navigateToPage]);

  // Mark row as reviewed/worked
  const toggleRowReviewed = useCallback((rowId) => {
    setReviewedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(rowId)) {
        newSet.delete(rowId);
      } else {
        newSet.add(rowId);
      }
      return newSet;
    });
  }, []);

  // Count validation errors for badge
  const validationErrorCount = useMemo(() => {
    let count = 0;
    if (!balanceValidation.isValid) count++;
    if (meterValidation.hasErrors) count += meterValidation.errorMessages.length;
    if (usageValidation.hasErrors) count += usageValidation.errorMessages.length;
    if (uomValidation.hasErrors) count += uomValidation.errorMessages.length;
    return count;
  }, [balanceValidation, meterValidation, usageValidation, uomValidation]);

  const handleSave = async () => {
    if (!editMode) {
      // Entering edit mode - auto-assign to current user and set status to Review
      try {
        const { user } = getAuth();
        
        // Auto-assign to current user if not already assigned
        if (user && assignedTo !== user.id) {
          await api.patch(`/accounts/${currentAccount.id}/documents/${documentId}/assign`, {
            user_id: user.id
          });
          setAssignedTo(user.id);
        }
        
        // Set status to Review if it's Approved
        if (reviewStatus === 'approved') {
          await api.patch(`/accounts/${currentAccount.id}/documents/${documentId}/status`, {
            review_status: 'review'
          });
          setReviewStatus('review');
          toast.info('Document unlocked for editing');
        }
        
        setEditMode(true);
        fetchAuditLogs();
      } catch (error) {
        toast.error('Failed to start editing');
        return;
      }
      return;
    }
    
    setSaving(true);
    try {
      // Client-side validation: due date >= issue date
      if (dueDateError) {
        toast.error(dueDateError);
        setSaving(false);
        return;
      }

      // Client-side validation: meter read date ordering
      if (meterValidation.hasErrors) {
        toast.error(meterValidation.errorMessages[0] || 'Please fix meter read date errors before saving');
        setSaving(false);
        return;
      }

      const updatedData = {
        ...statement,
        provider_charges: providerCharges,
        meter_reads: meterReads,
        usage_details: usageDetails,
        other_charges: otherCharges
      };
      
      const { data } = await api.put(
        `/accounts/${currentAccount.id}/documents/${documentId}/extracted-data`,
        updatedData
      );
      
      // Update confidence scores from response
      if (data.confidence_scores) {
        setConfidenceScores(data.confidence_scores);
      }
      if (Array.isArray(data.user_edited_fields)) {
        setUserEditedFields(data.user_edited_fields);
      }
      
      toast.success('Changes saved and validated');
      setEditMode(false);
      
      // Refresh document to get updated data
      fetchDocument();
      fetchAuditLogs();
    } catch (error) {
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  // Meter Reads functions
  const addMeterRead = () => {
    setMeterReads([...meterReads, {
      meter_number: '',
      commodity: 'Electricity',
      multiplier: 'N/A',
      previous_read_date: '',
      current_read_date: '',
      previous_read: '',
      current_read: '',
      read_type: 'Actual'
    }]);
  };

  const updateMeterRead = (index, field, value) => {
    const updated = [...meterReads];
    updated[index] = { ...updated[index], [field]: value };
    setMeterReads(updated);

    // Keep linked Usage Details in sync: a meter's billed demand and the
    // matching Demand-basis usage line(s) for that meter represent the SAME
    // demand reading, so editing one should update the other.
    if (field === 'billed_demand') {
      const mn = String(updated[index].meter_number || '').trim();
      if (mn) {
        setUsageDetails((prev) => prev.map((u) =>
          (u.billing_basis === 'Demand' && String(u.meter_number || '').trim() === mn)
            ? { ...u, usage: value }
            : u
        ));
      }
    }
  };

  const removeMeterRead = (index) => {
    setMeterReads(meterReads.filter((_, i) => i !== index));
  };

  // Usage Details functions
  const addUsageDetail = () => {
    setUsageDetails([...usageDetails, {
      meter_number: '',
      line_item_description: '',
      usage: '',
      uom: 'kWh',
      usage_rate: '',
      charges: ''
    }]);
  };

  const updateUsageDetail = (index, field, value) => {
    const updated = [...usageDetails];
    updated[index] = { ...updated[index], [field]: value };
    setUsageDetails(updated);
  };

  const removeUsageDetail = (index) => {
    setUsageDetails(usageDetails.filter((_, i) => i !== index));
  };

  // Other Charges functions
  const addOtherCharge = () => {
    setOtherCharges([...otherCharges, {
      line_item_description: '',
      usage: '',
      rate: '',
      charge: ''
    }]);
  };

  const updateOtherCharge = (index, field, value) => {
    const updated = [...otherCharges];
    updated[index] = { ...updated[index], [field]: value };
    setOtherCharges(updated);
  };

  const removeOtherCharge = (index) => {
    setOtherCharges(otherCharges.filter((_, i) => i !== index));
  };

  useEffect(() => {
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Inline "missing" red icon shown next to mandatory-field labels when the
  // parser could not extract the value. Only rendered for fields present in
  // MANDATORY_STATEMENT_FIELDS.
  const _EDIT_SYSTEM_FIELDS = new Set(['usage_summary', 'ai_validation_summary', 'service_summary', 'quality']);
  const prettyField = (k) => (({
    meter_reads: 'Meter Reads', usage_details: 'Usage Details', other_charges: 'Other Charges',
    provider_charges: 'Provider Charges', child_invoices: 'Child Accounts', gst: 'Tax / GST',
    account_no: 'Account No', invoice_no: 'Invoice No', total_amount_due: 'Total Amount Due',
    new_charges: 'New Charges', property_name: 'Property Name', vendor_name: 'Vendor Name',
  })[k] || k.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()));
  const editedFieldLabels = (userEditedFields || [])
    .filter((k) => !k.startsWith('_') && !_EDIT_SYSTEM_FIELDS.has(k))
    .map(prettyField);

  const renderMissing = (fieldKey) => (
    mandatoryFieldsValidation.missingKeys.has(fieldKey)
      ? (
        <span
          title="Missing / did not parse — please fill in"
          data-testid={`missing-icon-${fieldKey}`}
          className="inline-flex items-center"
          key={`missing-${fieldKey}`}
        >
          <AlertTriangle className="h-3.5 w-3.5 text-red-600" />
        </span>
      )
      : null
  );
  const renderEditedBadge = (fieldKey) => (
    (userEditedFields || []).includes(fieldKey)
      ? (
        <span
          title="You edited this field — a re-parse will keep your value"
          data-testid={`edited-badge-${fieldKey}`}
          className="inline-flex items-center gap-0.5 px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wide bg-blue-50 text-blue-700 border border-blue-200"
          key={`edited-${fieldKey}`}
        >
          <Edit className="h-2.5 w-2.5" />Edited
        </span>
      )
      : null
  );

  return (
    <div className="h-screen flex flex-col bg-background">
      {reparseConfirmDialog}
      {/* Failed Document Banner */}
      {document?.status === 'failed' && (
        <div className="bg-destructive/10 border-b border-destructive/20 px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-destructive" />
            <div>
              <p className="text-sm font-medium text-destructive">Document Extraction Failed</p>
              <p className="text-xs text-muted-foreground">The PDF could not be automatically parsed. You can re-parse or manually enter the data.</p>
            </div>
          </div>
          <Button
            size="sm"
            className="rounded-none"
            onClick={() => handleReparse('all')}
            disabled={reparsing}
          >
            {reparsing ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            {reparsing ? 'Re-parsing...' : 'Try Again'}
          </Button>
        </div>
      )}
      
      {/* Header */}
      <header className="border-b border-border bg-card px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            className="rounded-none"
            onClick={() => navigate((typeof window !== 'undefined' && window.history.state && window.history.state.usr && window.history.state.usr.from) || '/bill-jobs')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5" strokeWidth={1.5} />
            <h1 className="font-mono text-sm font-medium">{privacy.fileName(document?.filename)}</h1>
            {hasBillPortalExport && (
              <Badge variant="outline" className="rounded-none text-[10px] px-1 py-0 bg-emerald-50 text-emerald-700 border-emerald-200">
                BP
              </Badge>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Download PDF Button */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (pdfUrl) {
                      const link = window.document.createElement('a');
                      link.href = pdfUrl;
                      link.download = privacy.fileName(document?.filename) || 'bill.pdf';
                      link.click();
                    }
                  }}
                  disabled={!pdfUrl}
                  className="rounded-none"
                  data-testid="download-bill"
                >
                  <Download className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-xs">Download PDF</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          
          {/* Download JSON Button */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDownloadJSON}
                  disabled={document?.status !== 'completed'}
                  className="rounded-none"
                  data-testid="download-json"
                >
                  <FileText className="h-4 w-4" />
                  <span className="ml-1 text-xs">JSON</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-xs">{hasBillPortalExport ? 'Download BillPortal JSON' : 'Convert & Download JSON'}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="rounded-none"
                disabled={reparsing || document?.status === 'processing'}
                title={reparseLocked ? 'This bill has already been re-parsed once. Your manual edits are preserved — a forced type can still be fixed by post-processing.' : undefined}
                data-testid="reparse-button"
              >
                {reparsing || document?.status === 'processing' ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                {reparsing ? 'Re-parsing...' : document?.status === 'processing' ? 'Processing...' : reparseLocked ? 'Re-parsed' : 'Re-parse'}
                <ChevronDown className="h-4 w-4 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="rounded-none">
              <DropdownMenuItem onClick={() => handleReparse('all')} disabled={reparseLocked} className="cursor-pointer" data-testid="reparse-all-fields">
                <RefreshCw className="h-4 w-4 mr-2" />
                Re-parse All Fields
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleReparse('missing')} disabled={reparseLocked} className="cursor-pointer" data-testid="reparse-missing-fields">
                <AlertCircle className="h-4 w-4 mr-2" />
                Fill Missing Fields Only
              </DropdownMenuItem>
              <ReparseTypeItems onPick={(t) => handleReparse('all', t)} testIdPrefix="viewer-reparse-as" />
            </DropdownMenuContent>
          </DropdownMenu>
          
          {/* Audit Trail Button */}
          <Button
            variant="outline"
            size="sm"
            className="rounded-none"
            onClick={() => { fetchAuditLogs(); setShowAuditModal(true); }}
          >
            <History className="h-4 w-4 mr-2" />
            History
          </Button>
          
          {editMode && (
            <Button
              variant="outline"
              size="sm"
              className="rounded-none"
              onClick={() => {
                setEditMode(false);
                fetchDocument();
              }}
            >
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
          )}
          <Button
            variant={editMode ? 'default' : 'outline'}
            size="sm"
            className="rounded-none font-bold uppercase"
            onClick={handleSave}
            disabled={saving}
            data-testid="viewer-edit-save-btn"
            title={reviewStatus === 'approved' && !editMode ? 'This bill is Validated & Locked. Click to unlock and edit — it will re-validate on save.' : ''}
          >
            {saving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : editMode ? (
              <Save className="h-4 w-4 mr-2" />
            ) : reviewStatus === 'approved' ? (
              <Lock className="h-4 w-4 mr-2" />
            ) : (
              <Edit className="h-4 w-4 mr-2" />
            )}
            {saving ? 'Saving...' : editMode ? 'Save' : reviewStatus === 'approved' ? 'Unlock & Edit' : 'Edit'}
          </Button>
        </div>
      </header>
      
      {/* Workflow Bar */}
      <div className="border-b border-border bg-card px-6 py-2 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Label className="text-xs uppercase font-medium">Assigned:</Label>
            <Select value={assignedTo || '__unassigned__'} onValueChange={(value) => handleAssignment(value === '__unassigned__' ? null : value)}>
              <SelectTrigger className="w-40 h-8 rounded-none text-xs">
                <SelectValue placeholder="Unassigned" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__unassigned__">Unassigned</SelectItem>
                {accountUsers.map((user) => (
                  <SelectItem key={user.id} value={user.id}>
                    {user.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        {!canApprove ? (
          <Badge className="rounded-none bg-amber-500 hover:bg-amber-500" data-testid="validation-status-badge">
            <AlertTriangle className="h-3 w-3 mr-1" />
            Validation Failed
          </Badge>
        ) : reviewStatus === 'approved' ? (
          <Badge className="rounded-none bg-emerald-600" data-testid="validation-status-badge">
            <Lock className="h-3 w-3 mr-1" />
            Validated &amp; Locked
          </Badge>
        ) : (
          <Badge variant="outline" className="rounded-none" data-testid="validation-status-badge">
            <Clock className="h-3 w-3 mr-1" />
            Validating
          </Badge>
        )}
      </div>

      {/* Split View */}
      <ResizablePanelGroup direction="horizontal" className="flex-1">
        {/* PDF Panel */}
        <ResizablePanel defaultSize={45} minSize={30}>
          <div className="h-full flex flex-col bg-muted/30">
            {/* PDF Controls */}
            <div className="flex items-center justify-between px-4 py-2 border-b border-border bg-card">
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setScale(s => Math.max(0.5, s - 0.1))}
                  className="rounded-none h-8 w-8 p-0"
                  data-testid="zoom-out"
                >
                  <ZoomOut className="h-4 w-4" />
                </Button>
                <span className="text-sm font-mono w-14 text-center">{Math.round(scale * 100)}%</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setScale(s => Math.min(2.0, s + 0.1))}
                  className="rounded-none h-8 w-8 p-0"
                  data-testid="zoom-in"
                >
                  <ZoomIn className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setScale(1.0)}
                  className="rounded-none text-xs"
                >
                  Reset
                </Button>
              </div>
              
              {/* Rotation Controls */}
              <div className="flex items-center gap-1 border-l border-border pl-2 ml-2">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setRotation(r => (r - 90 + 360) % 360)}
                        className="rounded-none h-8 w-8 p-0"
                        data-testid="rotate-ccw"
                      >
                        <RotateCcw className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Rotate Left</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <span className="text-xs font-mono w-10 text-center">{rotation}°</span>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setRotation(r => (r + 90) % 360)}
                        className="rounded-none h-8 w-8 p-0"
                        data-testid="rotate-cw"
                      >
                        <RotateCw className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Rotate Right</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              
              <div className="flex items-center gap-2">
                {numPages && (
                  <span className="text-xs text-muted-foreground">
                    {numPages} page{numPages > 1 ? 's' : ''}
                  </span>
                )}
              </div>
            </div>
            
            {/* JPEG2000 Warning Banner */}
            {hasJpeg2000Warning && (
              <div className="mx-4 mb-2 p-3 bg-yellow-50 border border-yellow-200 rounded-none">
                <div className="flex items-center gap-2 text-yellow-800">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                  <div className="text-xs">
                    <p className="font-medium">Some images in this PDF may not display</p>
                    <p className="text-yellow-700">This PDF uses JPEG2000 encoding which has limited browser support. The data extraction is complete.</p>
                    <Button 
                      variant="link" 
                      size="sm" 
                      className="h-auto p-0 text-yellow-800 underline"
                      onClick={() => pdfUrl && window.open(pdfUrl, '_blank')}
                    >
                      Download PDF to view in desktop viewer
                    </Button>
                  </div>
                </div>
              </div>
            )}
            
            {/* PDF Content */}
            <div className="flex-1 overflow-auto p-4" ref={pdfContainerRef}>
              <div className="mx-auto" style={{ maxWidth: `${Math.round(650 * scale)}px` }}>
                {pdfUrl ? (
                  <Document
                    file={pdfUrl}
                    onLoadSuccess={onDocumentLoadSuccess}
                    options={pdfOptions}
                    loading={
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                      </div>
                    }
                    error={
                      <div className="flex flex-col items-center justify-center py-12 text-center px-4">
                        <FileText className="h-12 w-12 mb-4 text-muted-foreground" />
                        <p className="text-muted-foreground mb-2">Failed to load PDF</p>
                        {pdfLoadError && (
                          <p className="text-xs text-muted-foreground max-w-xs">{pdfLoadError}</p>
                        )}
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="mt-4"
                          onClick={() => pdfUrl && window.open(pdfUrl, '_blank')}
                        >
                          <Download className="h-4 w-4 mr-2" />
                          Download PDF
                        </Button>
                      </div>
                    }
                    onLoadError={(error) => {
                      console.error('PDF Load Error:', error);
                      setPdfLoadError(error?.message || 'Unknown error');
                    }}
                  >
                    {Array.from(new Array(numPages), (el, index) => (
                      <LazyPdfPage
                        key={`page_${index + 1}`}
                        pageNumber={index + 1}
                        className="mb-4 border border-border transition-all"
                        isActive={currentPage === index + 1}
                        width={Math.round(650 * scale)}
                        scale={scale}
                        rotation={rotation}
                        estimatedHeight={Math.round(650 * 1.294)}
                        onRenderError={(error) => {
                          console.warn('Page render error:', error);
                          if (error?.message?.includes('JpxError') || error?.message?.includes('OpenJPEG')) {
                            setHasJpeg2000Warning(true);
                          }
                        }}
                      />
                    ))}
                  </Document>
                ) : (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                  </div>
                )}
              </div>
            </div>
          </div>
        </ResizablePanel>

        <ResizableHandle className="w-1 bg-border hover:bg-primary/50 transition-colors" />

        {/* Data Panel with Tabs */}
        <ResizablePanel defaultSize={55} minSize={40}>
          <div className="h-full overflow-auto bg-background">
            <Tabs defaultValue="statement" className="w-full h-full flex flex-col">
              <TabsList className="w-full grid grid-cols-5 rounded-none border-b">
                <TabsTrigger value="statement" className="rounded-none">Statement</TabsTrigger>
                <TabsTrigger value="meter-reads" className="rounded-none">Meter Reads</TabsTrigger>
                <TabsTrigger value="usage" className="rounded-none">Usage Details</TabsTrigger>
                <TabsTrigger value="other" className="rounded-none">Other Charges</TabsTrigger>
                <TabsTrigger value="validation" className="rounded-none" data-testid="validation-tab-trigger">
                  <span className="flex items-center gap-1">
                    Validation
                    {(!mandatoryFieldsValidation.allPresent) ? (
                      <AlertTriangle className="h-3 w-3 text-red-600" data-testid="validation-tab-missing-mandatory" />
                    ) : intelligentValidation.bothValid ? (
                      <Check className="h-3 w-3 text-green-600" />
                    ) : (
                      <AlertTriangle className="h-3 w-3 text-yellow-600" />
                    )}
                  </span>
                </TabsTrigger>
              </TabsList>

              {/* Statement Tab */}
              <TabsContent value="statement" className="flex-1 overflow-auto p-6">
                <h3 className="text-lg font-bold mb-4">Bill Statement</h3>
                <div className="mb-5">
                  <EmissionsCard parsed={{ commodity: statement.commodity, usage_details: usageDetails, meter_reads: meterReads, primary_service_state: statement.state, postcode: statement.postcode, country: statement.country, issue_date: statement.issue_date }} />
                </div>
                {editedFieldLabels.length > 0 && (
                  <div className="mb-4 flex items-start gap-2 border border-blue-200 bg-blue-50 px-3 py-2" data-testid="edited-fields-banner">
                    <Lock className="h-3.5 w-3.5 text-blue-700 mt-0.5 shrink-0" />
                    <div className="text-xs text-blue-900">
                      <span className="font-semibold">Protected on re-parse:</span>{' '}
                      {editedFieldLabels.join(' · ')}
                      <span className="block text-[11px] text-blue-700/80 mt-0.5">These fields you edited will be kept if this bill is re-parsed.</span>
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs uppercase tracking-wide font-medium">Property Name</Label>
                      <ConfidenceIndicator confidence={confidenceScores.property_name} field="property_name" />
                      {renderMissing("property_name")}
                      {renderEditedBadge("property_name")}
                    </div>
                    <Input
                      className="rounded-none mt-1 font-mono text-sm"
                      value={statement.property_name}
                      onChange={(e) => setStatement({ ...statement, property_name: e.target.value })}
                      disabled={!editMode}
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs uppercase tracking-wide font-medium">Vendor Name</Label>
                      <ConfidenceIndicator confidence={confidenceScores.vendor_name} field="vendor_name" />
                      {renderMissing("vendor_name")}
                      {renderEditedBadge("vendor_name")}
                    </div>
                    <Input
                      className="rounded-none mt-1 font-mono text-sm"
                      value={statement.vendor_name}
                      onChange={(e) => setStatement({ ...statement, vendor_name: e.target.value })}
                      disabled={!editMode}
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs uppercase tracking-wide font-medium">Account No</Label>
                      <ConfidenceIndicator confidence={confidenceScores.account_no} field="account_no" />
                      {renderMissing("account_no")}
                      {renderEditedBadge("account_no")}
                    </div>
                    <Input
                      className="rounded-none mt-1 font-mono text-sm"
                      value={statement.account_no}
                      onChange={(e) => setStatement({ ...statement, account_no: e.target.value })}
                      disabled={!editMode}
                      data-testid="statement-account-no"
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs uppercase tracking-wide font-medium">Org Name / Bill To Name</Label>
                      <ConfidenceIndicator confidence={confidenceScores.customer_name} field="customer_name" />
                      {renderMissing("customer_name")}
                      {renderEditedBadge("customer_name")}
                    </div>
                    <Input
                      className="rounded-none mt-1 font-mono text-sm"
                      value={statement.customer_name}
                      onChange={(e) => setStatement({ ...statement, customer_name: e.target.value })}
                      disabled={!editMode}
                      data-testid="statement-customer-name"
                    />
                  </div>
                  <div className="col-span-2">
                    <div className="flex items-center gap-2">
                      <Label className="text-xs uppercase tracking-wide font-medium">Billing Address</Label>
                      <ConfidenceIndicator confidence={confidenceScores.billing_address || confidenceScores.service_address} field="billing_address" />
                    </div>
                    <Input
                      className="rounded-none mt-1 font-mono text-sm"
                      value={statement.billing_address}
                      onChange={(e) => setStatement({ ...statement, billing_address: e.target.value })}
                      disabled={!editMode}
                      data-testid="statement-billing-address"
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs uppercase tracking-wide font-medium">City</Label>
                      <ConfidenceIndicator confidence={confidenceScores.city} field="city" />
                    </div>
                    <Input
                      className="rounded-none mt-1 font-mono text-sm"
                      value={statement.city}
                      onChange={(e) => setStatement({ ...statement, city: e.target.value })}
                      disabled={!editMode}
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs uppercase tracking-wide font-medium">State</Label>
                      <ConfidenceIndicator confidence={confidenceScores.state} field="state" />
                    </div>
                    <Input
                      className="rounded-none mt-1 font-mono text-sm"
                      value={statement.state}
                      onChange={(e) => setStatement({ ...statement, state: e.target.value })}
                      disabled={!editMode}
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs uppercase tracking-wide font-medium">Postcode</Label>
                      <ConfidenceIndicator confidence={confidenceScores.postcode} field="postcode" />
                    </div>
                    <Input
                      className="rounded-none mt-1 font-mono text-sm"
                      value={statement.postcode}
                      onChange={(e) => setStatement({ ...statement, postcode: e.target.value })}
                      disabled={!editMode}
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs uppercase tracking-wide font-medium">Invoice No</Label>
                      <ConfidenceIndicator confidence={confidenceScores.invoice_no} field="invoice_no" />
                    </div>
                    <Input
                      className="rounded-none mt-1 font-mono text-sm"
                      value={statement.invoice_no}
                      onChange={handleInvoiceNoChange}
                      disabled={!editMode}
                      placeholder="Digits only (no letters)"
                      data-testid="statement-invoice-no"
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs uppercase tracking-wide font-medium">Issue Date</Label>
                      <ConfidenceIndicator confidence={confidenceScores.issue_date} field="issue_date" />
                      {renderMissing("issue_date")}
                      {renderEditedBadge("issue_date")}
                    </div>
                    <Input
                      type="date"
                      className="rounded-none mt-1 font-mono text-sm"
                      value={statement.issue_date}
                      onChange={(e) => setStatement({ ...statement, issue_date: e.target.value })}
                      disabled={!editMode}
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs uppercase tracking-wide font-medium">Period From</Label>
                      <ConfidenceIndicator confidence={confidenceScores.period_from} field="period_from" />
                      {renderMissing("period_from")}
                      {renderEditedBadge("period_from")}
                    </div>
                    <Input
                      type="date"
                      className="rounded-none mt-1 font-mono text-sm"
                      value={statement.period_from}
                      onChange={(e) => setStatement({ ...statement, period_from: e.target.value })}
                      disabled={!editMode}
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs uppercase tracking-wide font-medium">Period To</Label>
                      <ConfidenceIndicator confidence={confidenceScores.period_to} field="period_to" />
                      {renderMissing("period_to")}
                      {renderEditedBadge("period_to")}
                    </div>
                    <Input
                      type="date"
                      className="rounded-none mt-1 font-mono text-sm"
                      value={statement.period_to}
                      onChange={(e) => setStatement({ ...statement, period_to: e.target.value })}
                      disabled={!editMode}
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs uppercase tracking-wide font-medium">Due Date</Label>
                      <ConfidenceIndicator confidence={confidenceScores.due_date} field="due_date" />
                    </div>
                    <Input
                      type="date"
                      className={`rounded-none mt-1 font-mono text-sm ${dueDateError ? 'border-red-500' : ''}`}
                      value={statement.due_date}
                      onChange={(e) => setStatement({ ...statement, due_date: e.target.value })}
                      disabled={!editMode}
                      min={statement.issue_date || undefined}
                      data-testid="statement-due-date"
                    />
                    {dueDateError && (
                      <p className="text-xs text-red-600 mt-1" data-testid="due-date-error">{dueDateError}</p>
                    )}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs uppercase tracking-wide font-medium">Previous Balance</Label>
                      <ConfidenceIndicator confidence={confidenceScores.previous_balance} field="previous_balance" />
                    </div>
                    <Input
                      className="rounded-none mt-1 font-mono text-sm"
                      inputMode="decimal"
                      value={statement.previous_balance}
                      onChange={handleAmountChange('previous_balance')}
                      disabled={!editMode}
                      data-testid="statement-previous-balance"
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs uppercase tracking-wide font-medium">Payments Received</Label>
                      <ConfidenceIndicator confidence={confidenceScores.payments_received} field="payments_received" />
                    </div>
                    <Input
                      className="rounded-none mt-1 font-mono text-sm"
                      inputMode="decimal"
                      value={statement.payments_received}
                      onChange={handleAmountChange('payments_received')}
                      disabled={!editMode}
                      data-testid="statement-payments-received"
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs uppercase tracking-wide font-medium">Balance Forward</Label>
                      <ConfidenceIndicator confidence={confidenceScores.balance_forward} field="balance_forward" />
                    </div>
                    <Input
                      className="rounded-none mt-1 font-mono text-sm"
                      inputMode="decimal"
                      value={statement.balance_forward}
                      onChange={handleAmountChange('balance_forward')}
                      disabled={!editMode}
                      data-testid="statement-balance-forward"
                    />
                  </div>
                  
                  {/* Late Fee, Credits, Adjustments Row */}
                  <div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs uppercase tracking-wide font-medium">Late Fee</Label>
                      <ConfidenceIndicator confidence={confidenceScores.late_fee} field="late_fee" />
                    </div>
                    <Input
                      className="rounded-none mt-1 font-mono text-sm"
                      inputMode="decimal"
                      value={statement.late_fee}
                      onChange={handleAmountChange('late_fee')}
                      disabled={!editMode}
                      placeholder="0.00"
                      data-testid="statement-late-fee"
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs uppercase tracking-wide font-medium">Credits</Label>
                      <ConfidenceIndicator confidence={confidenceScores.credits} field="credits" />
                    </div>
                    <Input
                      className="rounded-none mt-1 font-mono text-sm"
                      inputMode="decimal"
                      value={statement.credits}
                      onChange={handleAmountChange('credits')}
                      disabled={!editMode}
                      placeholder="0.00"
                      data-testid="statement-credits"
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs uppercase tracking-wide font-medium">Adjustments</Label>
                      <ConfidenceIndicator confidence={confidenceScores.adjustments} field="adjustments" />
                    </div>
                    <Input
                      className="rounded-none mt-1 font-mono text-sm"
                      inputMode="decimal"
                      value={statement.adjustments}
                      onChange={handleAmountChange('adjustments')}
                      disabled={!editMode}
                      placeholder="0.00"
                      data-testid="statement-adjustments"
                    />
                  </div>
                  
                  <div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs uppercase tracking-wide font-medium">GST</Label>
                      <ConfidenceIndicator confidence={confidenceScores.gst} field="gst" />
                    </div>
                    <Input
                      className="rounded-none mt-1 font-mono text-sm bg-slate-50"
                      inputMode="decimal"
                      value={statement.gst}
                      onChange={handleAmountChange('gst')}
                      disabled={!editMode}
                      placeholder="0.00"
                      data-testid="statement-gst"
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs uppercase tracking-wide font-medium">New Charges</Label>
                      <ConfidenceIndicator confidence={confidenceScores.new_charges} field="new_charges" />
                      {renderMissing("new_charges")}
                      {renderEditedBadge("new_charges")}
                    </div>
                    <Input
                      className="rounded-none mt-1 font-mono text-sm"
                      inputMode="decimal"
                      value={statement.new_charges}
                      onChange={handleAmountChange('new_charges')}
                      disabled={!editMode}
                      data-testid="statement-new-charges"
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs uppercase tracking-wide font-medium">Total Amount Due</Label>
                      <ConfidenceIndicator confidence={confidenceScores.total_amount_due} field="total_amount_due" />
                      {renderMissing("total_amount_due")}
                      {renderEditedBadge("total_amount_due")}
                    </div>
                    <Input
                      className="rounded-none mt-1 font-mono text-sm font-bold"
                      inputMode="decimal"
                      value={statement.total_amount_due}
                      onChange={handleAmountChange('total_amount_due')}
                      disabled={!editMode}
                      data-testid="statement-total-amount-due"
                    />
                  </div>
                </div>
                
                {/* Provider Charges Section (for split-billing bills) */}
                {providerCharges.length > 0 && (
                  <div className="mt-8">
                    <h4 className="text-md font-bold mb-3 flex items-center gap-2">
                      Provider Charges
                      <Badge variant="secondary" className="rounded-none text-xs">
                        {providerCharges.length} Provider{providerCharges.length > 1 ? 's' : ''}
                      </Badge>
                    </h4>
                    <div className="overflow-x-auto border border-border">
                      <table className="w-full border-collapse" style={{ minWidth: '600px' }}>
                        <thead className="bg-muted">
                          <tr>
                            <th className="border border-border p-2 text-left text-xs font-medium">Provider</th>
                            <th className="border border-border p-2 text-left text-xs font-medium">Type</th>
                            <th className="border border-border p-2 text-left text-xs font-medium">Invoice No</th>
                            <th className="border border-border p-2 text-right text-xs font-medium">New Charges</th>
                            <th className="border border-border p-2 text-right text-xs font-medium">Tax</th>
                            <th className="border border-border p-2 text-right text-xs font-medium">Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {providerCharges.map((provider, index) => (
                            <tr key={index}>
                              <td className="border border-border p-2 text-sm font-medium">{provider.provider_name || '-'}</td>
                              <td className="border border-border p-2 text-sm">
                                <Badge variant="outline" className="rounded-none text-xs">
                                  {provider.service_type || 'Both'}
                                </Badge>
                              </td>
                              <td className="border border-border p-2 text-sm font-mono">{provider.invoice_no || '-'}</td>
                              <td className="border border-border p-2 text-sm text-right font-mono">
                                {provider.new_charges ? `$${parseFloat(provider.new_charges).toFixed(2)}` : '-'}
                              </td>
                              <td className="border border-border p-2 text-sm text-right font-mono">
                                {provider.sales_tax ? `$${parseFloat(provider.sales_tax).toFixed(2)}` : '-'}
                              </td>
                              <td className="border border-border p-2 text-sm text-right font-mono font-medium">
                                {provider.total_charges ? `$${parseFloat(provider.total_charges).toFixed(2)}` : '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </TabsContent>

              {/* Meter Reads Tab */}
              <TabsContent value="meter-reads" className="flex-1 overflow-auto p-6">
                <DemandSummaryCard usageSummary={usageSummary} />
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <h3 className="text-lg font-bold">Meter Reads</h3>
                  </div>
                  {editMode && (
                    <Button size="sm" variant="outline" className="rounded-none" onClick={addMeterRead}>
                      <Plus className="h-4 w-4 mr-1" /> Add Row
                    </Button>
                  )}
                </div>
                
                {meterReads.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-12">No meter reads data</p>
                ) : (
                  <>
                    <div className="overflow-x-auto border border-border">
                      <table className="border-collapse" style={{ minWidth: '1400px' }}>
                        <thead className="bg-muted">
                          <tr>
                            <th className="border border-border p-2 text-left text-xs font-medium whitespace-nowrap" style={{ minWidth: '50px' }}>Page</th>
                            <th className="border border-border p-2 text-left text-xs font-medium whitespace-nowrap" style={{ minWidth: '110px' }}>Meter No</th>
                            <th className="border border-border p-2 text-left text-xs font-medium whitespace-nowrap" style={{ minWidth: '90px' }}>Commodity</th>
                            <th className="border border-border p-2 text-left text-xs font-medium whitespace-nowrap" style={{ minWidth: '80px' }}>Multiplier</th>
                            <th className="border border-border p-2 text-left text-xs font-medium whitespace-nowrap" style={{ minWidth: '100px' }}>Prev Date</th>
                            <th className="border border-border p-2 text-left text-xs font-medium whitespace-nowrap" style={{ minWidth: '100px' }}>Curr Date</th>
                            <th className="border border-border p-2 text-left text-xs font-medium whitespace-nowrap" style={{ minWidth: '90px' }}>Prev Read</th>
                            <th className="border border-border p-2 text-left text-xs font-medium whitespace-nowrap" style={{ minWidth: '90px' }}>Curr Read</th>
                            <th className="border border-border p-2 text-left text-xs font-medium whitespace-nowrap" style={{ minWidth: '90px' }}>Type</th>
                            <th className="border border-border p-2 text-left text-xs font-medium whitespace-nowrap bg-amber-50" style={{ minWidth: '100px' }}>Billed Demand</th>
                            <th className="border border-border p-2 text-left text-xs font-medium whitespace-nowrap bg-amber-50" style={{ minWidth: '80px' }}>Dmd UOM</th>
                            <th className="border border-border p-2 text-left text-xs font-medium whitespace-nowrap bg-amber-50" style={{ minWidth: '90px' }}>Dmd Type</th>
                            {editMode && <th className="border border-border p-2 w-20 text-center text-xs font-medium">Actions</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {meterReads.map((read, index) => {
                            const rowId = `meter-${index}`;
                            const isRowActive = editingRowId === rowId;
                            const isRowReviewed = reviewedRows.has(rowId);
                            return (
                              <Fragment key={index}>
                              <tr 
                                className={`transition-colors ${isRowActive ? 'bg-blue-50 ring-2 ring-blue-400 ring-inset' : ''} ${isRowReviewed ? 'bg-green-50' : ''} ${!editMode && read.page_number ? 'cursor-pointer hover:bg-muted/50' : ''}`}
                                onClick={() => !editMode && read.page_number && navigateToPage(read.page_number, read.meter_number)}
                                title={read.page_number ? `Click to go to page ${read.page_number}` : ''}
                              >
                                <td className="border border-border p-1 text-center">
                                  {read.page_number && (
                                    <Badge 
                                      variant="outline" 
                                      className={`rounded-none text-xs font-mono cursor-pointer hover:bg-muted ${isRowActive ? 'bg-blue-100' : ''}`}
                                      onClick={(e) => { e.stopPropagation(); navigateToPage(read.page_number, read.meter_number); }}
                                    >
                                      p.{read.page_number}
                                    </Badge>
                                  )}
                                </td>
                                <td className="border border-border p-1">
                                  <Input
                                    className="rounded-none border-0 font-mono text-xs"
                                    value={read.meter_number || ''}
                                    onChange={(e) => updateMeterRead(index, 'meter_number', e.target.value)}
                                    onFocus={() => editMode && handleRowFocus(rowId, read.page_number)}
                                    disabled={!editMode}
                                  />
                                </td>
                                <td className="border border-border p-1">
                                  <Select
                                    value={normalizeCommodity(read.commodity)}
                                    onValueChange={(value) => updateMeterRead(index, 'commodity', value)}
                                    onOpenChange={(open) => open && editMode && handleRowFocus(rowId, read.page_number)}
                                    disabled={!editMode}
                                  >
                                    <SelectTrigger className="rounded-none border-0 h-8 text-xs">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="Electricity">Electricity</SelectItem>
                                      <SelectItem value="Gas">Gas</SelectItem>
                                      <SelectItem value="Water">Water</SelectItem>
                                      <SelectItem value="Sewer">Sewer</SelectItem>
                                      <SelectItem value="Waste">Waste</SelectItem>
                                      <SelectItem value="Internet">Internet</SelectItem>
                                      <SelectItem value="Heating Oil">Heating Oil</SelectItem>
                                      <SelectItem value="Other">Other</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </td>
                                <td className="border border-border p-1">
                                  <Input
                                    className="rounded-none border-0 font-mono text-xs"
                                    inputMode="decimal"
                                    value={read.multiplier || 'N/A'}
                                    onChange={(e) => {
                                      const v = e.target.value;
                                      // Allow N/A placeholder, empty, or numeric with optional decimal
                                      if (v === '' || v === 'N/A' || /^\d*\.?\d*$/.test(v)) {
                                        updateMeterRead(index, 'multiplier', v);
                                      }
                                    }}
                                    onFocus={() => editMode && handleRowFocus(rowId, read.page_number)}
                                    disabled={!editMode}
                                    placeholder="N/A"
                                    data-testid={`meter-multiplier-${index}`}
                                  />
                                </td>
                                <td className="border border-border p-1">
                                  <Input
                                    type="date"
                                    className="rounded-none border-0 font-mono text-xs"
                                    value={read.previous_read_date || ''}
                                    onChange={(e) => updateMeterRead(index, 'previous_read_date', e.target.value)}
                                    onFocus={() => editMode && handleRowFocus(rowId, read.page_number)}
                                    disabled={!editMode}
                                  />
                                </td>
                                <td className="border border-border p-1">
                                  <Input
                                    type="date"
                                    className={`rounded-none border-0 font-mono text-xs ${meterValidation.validations[index] && !meterValidation.validations[index].isValid ? 'ring-2 ring-red-500' : ''}`}
                                    value={read.current_read_date || ''}
                                    onChange={(e) => updateMeterRead(index, 'current_read_date', e.target.value)}
                                    disabled={!editMode}
                                    min={read.previous_read_date || undefined}
                                    data-testid={`meter-current-read-date-${index}`}
                                  />
                                  {meterValidation.validations[index] && !meterValidation.validations[index].isValid && (
                                    <p className="text-[10px] text-red-600 mt-0.5" data-testid={`meter-date-error-${index}`}>
                                      Current date must be after previous
                                    </p>
                                  )}
                                </td>
                                <td className="border border-border p-1">
                                  <Input
                                    className="rounded-none border-0 font-mono text-xs"
                                    value={read.previous_read || ''}
                                    onChange={(e) => updateMeterRead(index, 'previous_read', e.target.value)}
                                    onFocus={() => editMode && handleRowFocus(rowId, read.page_number)}
                                    disabled={!editMode}
                                  />
                                </td>
                                <td className="border border-border p-1">
                                  <Input
                                    className="rounded-none border-0 font-mono text-xs"
                                    value={read.current_read || ''}
                                    onChange={(e) => updateMeterRead(index, 'current_read', e.target.value)}
                                    onFocus={() => editMode && handleRowFocus(rowId, read.page_number)}
                                    disabled={!editMode}
                                  />
                                </td>
                                <td className="border border-border p-1">
                                  <Select
                                    value={read.read_type || 'Actual'}
                                    onValueChange={(value) => updateMeterRead(index, 'read_type', value)}
                                    onOpenChange={(open) => open && editMode && handleRowFocus(rowId, read.page_number)}
                                    disabled={!editMode}
                                  >
                                    <SelectTrigger className="rounded-none border-0 h-8 text-xs">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="Actual">Actual</SelectItem>
                                      <SelectItem value="Estimated">Estimated</SelectItem>
                                      <SelectItem value="Customer">Customer</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </td>
                                <td className="border border-border p-1 bg-amber-50/40">
                                  <Input
                                    className="rounded-none border-0 font-mono text-xs"
                                    value={read.billed_demand ?? ''}
                                    onChange={(e) => updateMeterRead(index, 'billed_demand', e.target.value)}
                                    onFocus={() => editMode && handleRowFocus(rowId, read.page_number)}
                                    disabled={!editMode}
                                    placeholder="—"
                                  />
                                </td>
                                <td className="border border-border p-1 bg-amber-50/40">
                                  <Input
                                    className="rounded-none border-0 font-mono text-xs"
                                    value={read.demand_uom ?? ''}
                                    onChange={(e) => updateMeterRead(index, 'demand_uom', e.target.value)}
                                    onFocus={() => editMode && handleRowFocus(rowId, read.page_number)}
                                    disabled={!editMode}
                                    placeholder="kW"
                                  />
                                </td>
                                <td className="border border-border p-1 bg-amber-50/40">
                                  <Input
                                    className="rounded-none border-0 font-mono text-xs"
                                    value={read.demand_read_type ?? ''}
                                    onChange={(e) => updateMeterRead(index, 'demand_read_type', e.target.value)}
                                    onFocus={() => editMode && handleRowFocus(rowId, read.page_number)}
                                    disabled={!editMode}
                                    placeholder="—"
                                  />
                                </td>
                                {editMode && (
                                  <td className="border border-border p-1 text-center">
                                    <div className="flex items-center justify-center gap-1">
                                      <Button
                                        size="sm"
                                        variant={isRowReviewed ? "default" : "outline"}
                                        className={`h-6 w-6 p-0 rounded-none ${isRowReviewed ? 'bg-green-600 hover:bg-green-700' : ''}`}
                                        onClick={() => toggleRowReviewed(rowId)}
                                        title={isRowReviewed ? 'Marked as reviewed' : 'Mark as reviewed'}
                                      >
                                        <Check className="h-3 w-3" />
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        className="h-6 w-6 p-0"
                                        onClick={() => removeMeterRead(index)}
                                        title="Delete row"
                                      >
                                        <X className="h-4 w-4 text-red-500" />
                                      </Button>
                                    </div>
                                  </td>
                                )}
                              </tr>
                              {Array.isArray(read.reading_lines) && read.reading_lines.length > 0 && (
                                <tr data-testid={`meter-reading-lines-${index}`}>
                                  <td className="border border-border p-0 bg-muted/20" colSpan={editMode ? 13 : 12}>
                                    <div className="px-3 py-2" data-testid={`reading-lines-detail-${index}`}>
                                      <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                                        All meter readings ({read.reading_lines.length})
                                      </div>
                                      <table className="w-full text-[11px] border-collapse">
                                        <thead>
                                          <tr className="bg-muted/60 text-muted-foreground">
                                            <th className="border border-border px-2 py-1 text-left">Read Dates</th>
                                            <th className="border border-border px-2 py-1 text-left">Load Type</th>
                                            <th className="border border-border px-2 py-1 text-left">Reading Type</th>
                                            <th className="border border-border px-2 py-1 text-right">Previous</th>
                                            <th className="border border-border px-2 py-1 text-right">Present</th>
                                            <th className="border border-border px-2 py-1 text-right">Difference</th>
                                            <th className="border border-border px-2 py-1 text-right">Mult</th>
                                            <th className="border border-border px-2 py-1 text-right">Total Usage</th>
                                            <th className="border border-border px-2 py-1 text-left">UOM</th>
                                            <th className="border border-border px-2 py-1 text-left">Type</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {read.reading_lines.map((ln, li) => (
                                            <tr key={li} data-testid={`reading-line-${index}-${li}`} className="font-mono">
                                              <td className="border border-border px-2 py-1">{[ln.read_from, ln.read_to].filter(Boolean).join(' – ') || '—'}</td>
                                              <td className="border border-border px-2 py-1 font-sans">{ln.load_type || '—'}</td>
                                              <td className="border border-border px-2 py-1 font-sans font-medium">{ln.reading_type || '—'}</td>
                                              <td className="border border-border px-2 py-1 text-right">{ln.previous_read ?? '—'}</td>
                                              <td className="border border-border px-2 py-1 text-right">{ln.present_read ?? '—'}</td>
                                              <td className="border border-border px-2 py-1 text-right">{ln.difference ?? '—'}</td>
                                              <td className="border border-border px-2 py-1 text-right">{ln.multiplier ?? '—'}</td>
                                              <td className="border border-border px-2 py-1 text-right">{ln.total_usage ?? '—'}</td>
                                              <td className="border border-border px-2 py-1">{ln.uom || '—'}</td>
                                              <td className="border border-border px-2 py-1 font-sans">{ln.actual_estimated || '—'}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  </td>
                                </tr>
                              )}
                              </Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </TabsContent>

              {/* Usage Details Tab */}
              <TabsContent value="usage" className="flex-1 overflow-auto p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <h3 className="text-lg font-bold">Usage Details</h3>
                  </div>
                  {editMode && (
                    <Button size="sm" variant="outline" className="rounded-none" onClick={addUsageDetail}>
                      <Plus className="h-4 w-4 mr-1" /> Add Row
                    </Button>
                  )}
                </div>
                
                {usageDetails.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-12">No usage details</p>
                ) : (
                  <>
                    <div className="overflow-x-auto border border-border">
                      <table className="border-collapse" style={{ minWidth: '1200px' }}>
                        <thead className="bg-muted">
                          <tr>
                            <th className="border border-border p-2 text-left text-xs font-medium whitespace-nowrap" style={{ minWidth: '50px' }}>Page</th>
                            <th className="border border-border p-2 text-left text-xs font-medium whitespace-nowrap" style={{ minWidth: '100px' }}>Meter No</th>
                            <th className="border border-border p-2 text-left text-xs font-medium whitespace-nowrap" style={{ minWidth: '90px' }}>Commodity</th>
                            <th className="border border-border p-2 text-left text-xs font-medium whitespace-nowrap" style={{ minWidth: '80px' }}>Type</th>
                            <th className="border border-border p-2 text-left text-xs font-medium whitespace-nowrap" style={{ minWidth: '180px' }}>Description</th>
                            <th className="border border-border p-2 text-left text-xs font-medium whitespace-nowrap" style={{ minWidth: '80px' }}>Usage</th>
                            <th className="border border-border p-2 text-left text-xs font-medium whitespace-nowrap" style={{ minWidth: '70px' }}>UOM</th>
                            <th className="border border-border p-2 text-left text-xs font-medium whitespace-nowrap" style={{ minWidth: '80px' }}>Rate</th>
                            <th className="border border-border p-2 text-left text-xs font-medium whitespace-nowrap" style={{ minWidth: '90px' }}>Charge</th>
                            {editMode && <th className="border border-border p-2 w-20 text-center text-xs font-medium">Actions</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {usageDetails.map((item, index) => {
                            const rowId = `usage-${index}`;
                            const usageRowValidation = usageValidation.validations.find(v => v.index === index);
                            const uomRowValidation = uomValidation.validations.find(v => v.index === index);
                            const hasCalcError = usageRowValidation && !usageRowValidation.isValid;
                            const hasUomError = uomRowValidation && !uomRowValidation.isValid;
                            const isRowActive = editingRowId === rowId;
                            const isRowReviewed = reviewedRows.has(rowId);
                            return (
                              <tr 
                                key={index}
                                className={`transition-colors ${isRowActive ? 'bg-blue-50 ring-2 ring-blue-400 ring-inset' : ''} ${isRowReviewed ? 'bg-green-50' : ''} ${(hasCalcError || hasUomError) && showValidationWarnings && !isRowActive && !isRowReviewed ? (hasCalcError ? 'bg-yellow-50' : 'bg-orange-50') : ''} ${!editMode && item.page_number ? 'cursor-pointer hover:bg-muted/50' : ''}`}
                                onClick={() => !editMode && item.page_number && navigateToPage(item.page_number, item.line_item_description || item.meter_number)}
                                title={item.page_number ? `Click to go to page ${item.page_number}` : ''}
                              >
                                <td className="border border-border p-1 text-center">
                                  {item.page_number && (
                                    <Badge 
                                      variant="outline" 
                                      className={`rounded-none text-xs font-mono cursor-pointer hover:bg-muted ${isRowActive ? 'bg-blue-100' : ''}`}
                                      onClick={(e) => { e.stopPropagation(); navigateToPage(item.page_number, item.line_item_description || item.meter_number); }}
                                    >
                                      p.{item.page_number}
                                    </Badge>
                                  )}
                            </td>
                            <td className="border border-border p-1">
                              <Input
                                className="rounded-none border-0 font-mono text-xs"
                                value={item.meter_number || ''}
                                onChange={(e) => updateUsageDetail(index, 'meter_number', e.target.value)}
                                onFocus={() => editMode && handleRowFocus(rowId, item.page_number)}
                                disabled={!editMode}
                              />
                            </td>
                            <td className="border border-border p-1">
                              <Select
                                value={item.commodity || 'Electricity'}
                                onValueChange={(value) => updateUsageDetail(index, 'commodity', value)}
                                disabled={!editMode}
                              >
                                <SelectTrigger className="rounded-none border-0 h-8 text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="Electricity">Electricity</SelectItem>
                                  <SelectItem value="Gas">Gas</SelectItem>
                                  <SelectItem value="Water">Water</SelectItem>
                                  <SelectItem value="Sewer">Sewer</SelectItem>
                                  <SelectItem value="Waste">Waste</SelectItem>
                                  <SelectItem value="Internet">Internet</SelectItem>
                                  <SelectItem value="Heating Oil">Heating Oil</SelectItem>
                                  <SelectItem value="Phone">Phone</SelectItem>
                                  <SelectItem value="Cable/TV">Cable/TV</SelectItem>
                                  <SelectItem value="Other">Other</SelectItem>
                                </SelectContent>
                              </Select>
                            </td>
                            <td className="border border-border p-1">
                              <Select
                                value={item.usage_type || 'Both'}
                                onValueChange={(value) => updateUsageDetail(index, 'usage_type', value)}
                                disabled={!editMode}
                              >
                                <SelectTrigger className="rounded-none border-0 h-8 text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="Supply">Supply</SelectItem>
                                  <SelectItem value="Delivery">Delivery</SelectItem>
                                  <SelectItem value="Both">Both</SelectItem>
                                </SelectContent>
                              </Select>
                            </td>
                            <td className="border border-border p-1">
                              <div className="flex items-center gap-1">
                                <Input
                                  className="rounded-none border-0 text-xs"
                                  value={item.line_item_description || ''}
                                  onChange={(e) => updateUsageDetail(index, 'line_item_description', e.target.value)}
                                  disabled={!editMode}
                                />
                                {!editMode && <BillingBasisBadge basis={item.billing_basis} />}
                              </div>
                            </td>
                            <td className="border border-border p-1">
                              <Input
                                className="rounded-none border-0 font-mono text-xs"
                                value={item.usage || ''}
                                onChange={(e) => updateUsageDetail(index, 'usage', e.target.value)}
                                disabled={!editMode}
                              />
                            </td>
                            <td className="border border-border p-1">
                              {editMode ? (
                                <Select
                                  value={item.uom || 'kWh'}
                                  onValueChange={(value) => updateUsageDetail(index, 'uom', value)}
                                >
                                  <SelectTrigger className="rounded-none border-0 h-8 text-xs">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="kWh">kWh</SelectItem>
                                    <SelectItem value="Therms">Therms</SelectItem>
                                    <SelectItem value="CCF">CCF</SelectItem>
                                    <SelectItem value="Gallons">Gallons</SelectItem>
                                    <SelectItem value="MJ">MJ</SelectItem>
                                    <SelectItem value="Kg">Kg</SelectItem>
                                    <SelectItem value="tonnes">tonnes</SelectItem>
                                  </SelectContent>
                                </Select>
                              ) : (
                                <Input
                                  className="rounded-none border-0 font-mono text-xs"
                                  value={item.uom || ''}
                                  disabled
                                />
                              )}
                            </td>
                            <td className="border border-border p-1">
                              <Input
                                className="rounded-none border-0 font-mono text-xs"
                                value={item.usage_rate || ''}
                                onChange={(e) => updateUsageDetail(index, 'usage_rate', e.target.value)}
                                disabled={!editMode}
                              />
                            </td>
                            <td className="border border-border p-1">
                              <Input
                                className="rounded-none border-0 font-mono text-xs"
                                value={item.charges || ''}
                                onChange={(e) => updateUsageDetail(index, 'charges', e.target.value)}
                                onFocus={() => editMode && handleRowFocus(rowId, item.page_number)}
                                disabled={!editMode}
                              />
                            </td>
                            {editMode && (
                              <td className="border border-border p-1 text-center">
                                <div className="flex items-center justify-center gap-1">
                                  <Button
                                    size="sm"
                                    variant={isRowReviewed ? "default" : "outline"}
                                    className={`h-6 w-6 p-0 rounded-none ${isRowReviewed ? 'bg-green-600 hover:bg-green-700' : ''}`}
                                    onClick={() => toggleRowReviewed(rowId)}
                                    title={isRowReviewed ? 'Marked as reviewed' : 'Mark as reviewed'}
                                  >
                                    <Check className="h-3 w-3" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-6 w-6 p-0"
                                    onClick={() => removeUsageDetail(index)}
                                    title="Delete row"
                                  >
                                    <X className="h-4 w-4 text-red-500" />
                                  </Button>
                                </div>
                              </td>
                            )}
                          </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      
                      {/* Usage Total Row */}
                      <div className="border border-border border-t-0 bg-muted/50 p-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium">Total Usage Charges:</span>
                          <span className="text-lg font-bold font-mono">
                            ${billValidation.usageTotal.toFixed(2)}
                          </span>
                        </div>
                      </div>
                      
                      {/* Standardized usage — advanced/detailed view, collapsed
                          by default and shown only to Super Admin / Owner. */}
                      {isSuperAdmin() && (
                        <Accordion type="single" collapsible className="mb-3 border border-border border-t-0" data-testid="viewer-standardized-usage-accordion">
                          <AccordionItem value="std-usage" className="border-b-0">
                            <AccordionTrigger className="px-3 py-2 text-xs font-semibold uppercase tracking-wide hover:no-underline" data-testid="viewer-standardized-usage-trigger">
                              Standardized Usage (as-billed → reporting unit)
                            </AccordionTrigger>
                            <AccordionContent className="px-3 pb-3">
                              <StandardizedUsageCard commodity={statement.commodity} usageDetails={usageDetails} plain />
                            </AccordionContent>
                          </AccordionItem>
                        </Accordion>
                      )}

                      {/* Usage to New Charges Validation */}
                      <div className={`border border-border border-t-0 p-3 ${billValidation.lineItemsMatchNewCharges ? 'bg-green-50' : 'bg-yellow-50'}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {billValidation.lineItemsMatchNewCharges ? (
                              <Check className="h-4 w-4 text-green-600" />
                            ) : (
                              <AlertTriangle className="h-4 w-4 text-yellow-600" />
                            )}
                            <span className="text-sm font-medium">
                              Usage + Other Charges = New Charges
                            </span>
                          </div>
                          <div className="text-sm font-mono">
                            ${billValidation.usageTotal.toFixed(2)} + ${billValidation.otherChargesTotal.toFixed(2)} = ${billValidation.lineItemsSum.toFixed(2)}
                            {!billValidation.lineItemsMatchNewCharges && (
                              <span className="text-yellow-700 ml-2">
                                (Expected: ${billValidation.newCharges.toFixed(2)})
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Usage Cost Breakdown Accordion */}
                      {usageBreakdown.length > 0 && (
                        <UsageCostBreakdown 
                          usageBreakdown={usageBreakdown} 
                          otherCharges={otherCharges}
                          validationData={validationData}
                        />
                      )}
                    </div>
                  </>
                )}
              </TabsContent>

              {/* Other Charges Tab */}
              <TabsContent value="other" className="flex-1 overflow-auto p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold">Other Charges</h3>
                  {editMode && (
                    <Button size="sm" variant="outline" className="rounded-none" onClick={addOtherCharge}>
                      <Plus className="h-4 w-4 mr-1" /> Add Row
                    </Button>
                  )}
                </div>
                
                {otherCharges.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-12">No other charges</p>
                ) : (
                  <div className="overflow-x-auto border border-border">
                    <table className="border-collapse" style={{ minWidth: '800px' }}>
                      <thead className="bg-muted">
                        <tr>
                          <th className="border border-border p-2 text-left text-xs font-medium whitespace-nowrap" style={{ minWidth: '50px' }}>Page</th>
                          <th className="border border-border p-2 text-left text-xs font-medium whitespace-nowrap" style={{ minWidth: '250px' }}>Line Item Description</th>
                          <th className="border border-border p-2 text-left text-xs font-medium whitespace-nowrap" style={{ minWidth: '100px' }}>Usage</th>
                          <th className="border border-border p-2 text-left text-xs font-medium whitespace-nowrap" style={{ minWidth: '100px' }}>Rate</th>
                          <th className="border border-border p-2 text-left text-xs font-medium whitespace-nowrap" style={{ minWidth: '100px' }}>Charge</th>
                          {editMode && <th className="border border-border p-2 w-20 text-center text-xs font-medium">Actions</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {otherCharges.map((item, index) => {
                          const rowId = `other-${index}`;
                          const isRowActive = editingRowId === rowId;
                          const isRowReviewed = reviewedRows.has(rowId);
                          return (
                          <tr 
                            key={index}
                            className={`transition-colors ${isRowActive ? 'bg-blue-50 ring-2 ring-blue-400 ring-inset' : ''} ${isRowReviewed ? 'bg-green-50' : ''} ${!editMode && item.page_number ? 'cursor-pointer hover:bg-muted/50' : ''}`}
                            onClick={() => !editMode && item.page_number && navigateToPage(item.page_number, item.line_item_description)}
                            title={item.page_number ? `Click to go to page ${item.page_number}` : ''}
                          >
                            <td className="border border-border p-1 text-center">
                              {item.page_number && (
                                <Badge 
                                  variant="outline" 
                                  className={`rounded-none text-xs font-mono cursor-pointer hover:bg-muted ${isRowActive ? 'bg-blue-100' : ''}`}
                                  onClick={(e) => { e.stopPropagation(); navigateToPage(item.page_number, item.line_item_description); }}
                                >
                                  p.{item.page_number}
                                </Badge>
                              )}
                            </td>
                            <td className="border border-border p-1">
                              <Input
                                className="rounded-none border-0 font-mono text-xs"
                                value={item.line_item_description || ''}
                                onChange={(e) => updateOtherCharge(index, 'line_item_description', e.target.value)}
                                onFocus={() => editMode && handleRowFocus(rowId, item.page_number)}
                                disabled={!editMode}
                              />
                            </td>
                            <td className="border border-border p-1">
                              <Input
                                className="rounded-none border-0 font-mono text-xs"
                                value={item.usage || ''}
                                onChange={(e) => updateOtherCharge(index, 'usage', e.target.value)}
                                onFocus={() => editMode && handleRowFocus(rowId, item.page_number)}
                                disabled={!editMode}
                              />
                            </td>
                            <td className="border border-border p-1">
                              <Input
                                className="rounded-none border-0 font-mono text-xs"
                                value={item.rate || ''}
                                onChange={(e) => updateOtherCharge(index, 'rate', e.target.value)}
                                onFocus={() => editMode && handleRowFocus(rowId, item.page_number)}
                                disabled={!editMode}
                              />
                            </td>
                            <td className="border border-border p-1">
                              <Input
                                className="rounded-none border-0 font-mono text-xs"
                                value={item.charge || ''}
                                onChange={(e) => updateOtherCharge(index, 'charge', e.target.value)}
                                onFocus={() => editMode && handleRowFocus(rowId, item.page_number)}
                                disabled={!editMode}
                              />
                            </td>
                            {editMode && (
                              <td className="border border-border p-1 text-center">
                                <div className="flex items-center justify-center gap-1">
                                  <Button
                                    size="sm"
                                    variant={isRowReviewed ? "default" : "outline"}
                                    className={`h-6 w-6 p-0 rounded-none ${isRowReviewed ? 'bg-green-600 hover:bg-green-700' : ''}`}
                                    onClick={() => toggleRowReviewed(rowId)}
                                    title={isRowReviewed ? 'Marked as reviewed' : 'Mark as reviewed'}
                                  >
                                    <Check className="h-3 w-3" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-6 w-6 p-0"
                                    onClick={() => removeOtherCharge(index)}
                                    title="Delete row"
                                  >
                                    <X className="h-4 w-4 text-red-500" />
                                  </Button>
                                </div>
                              </td>
                            )}
                          </tr>
                        );
                        })}
                      </tbody>
                    </table>
                    
                    {/* Other Charges Total Row */}
                    <div className="border border-border border-t-0 bg-muted/50 p-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">Total Other Charges:</span>
                        <span className="text-lg font-bold font-mono">
                          ${billValidation.otherChargesTotal.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </TabsContent>

              {/* Validation Line Items Tab — always shown so every org sees reconciliation */}
              <TabsContent value="validation" className="flex-1 overflow-auto p-6" data-testid="validation-tab-content">
                  {/* Mandatory Fields Banner */}
                  {!mandatoryFieldsValidation.allPresent && (
                    <div className="mb-4 p-4 rounded-none border-2 bg-red-50 border-red-500" data-testid="mandatory-fields-warning">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="h-6 w-6 text-red-600 mt-0.5" />
                        <div className="flex-1">
                          <h3 className="font-bold text-lg text-red-800">
                            Missing Mandatory Statement Fields ({mandatoryFieldsValidation.missing.length} of {mandatoryFieldsValidation.total})
                          </h3>
                          <p className="text-sm text-red-700 mb-2">
                            The following statement-level fields did not parse and must be filled in:
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {mandatoryFieldsValidation.missing.map((f) => (
                              <Badge
                                key={f.key}
                                variant="destructive"
                                className="rounded-none"
                                data-testid={`missing-mandatory-${f.key}`}
                              >
                                {f.label}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Overall Status Banner — reconciliation summary (shown for all bills) */}
                  <>
                  <div className={`mb-4 p-4 rounded-none border-2 ${intelligentValidation.bothValid ? 'bg-green-50 border-green-500' : 'bg-yellow-50 border-yellow-500'}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {intelligentValidation.bothValid ? (
                          <Check className="h-6 w-6 text-green-600" />
                        ) : (
                          <AlertTriangle className="h-6 w-6 text-yellow-600" />
                        )}
                        <div>
                          <h3 className="font-bold text-lg">
                            {intelligentValidation.bothValid ? 'All Validations Passed' : 'Validation Issues Found'}
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            New Charges: {intelligentValidation.validationMatchesNewCharges ? '✓ Valid' : '✗ Mismatch'} | 
                            Total Due: {intelligentValidation.totalDueMatches ? '✓ Valid' : '✗ Mismatch'}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Badge 
                          variant={intelligentValidation.validationMatchesNewCharges ? "default" : "destructive"}
                          className={`rounded-none ${intelligentValidation.validationMatchesNewCharges ? 'bg-green-600' : ''}`}
                        >
                          New Charges: {intelligentValidation.validationMatchesNewCharges ? 'VALID' : 'MISMATCH'}
                        </Badge>
                        <Badge 
                          variant={intelligentValidation.totalDueMatches ? "default" : "destructive"}
                          className={`rounded-none ${intelligentValidation.totalDueMatches ? 'bg-green-600' : ''}`}
                        >
                          Total Due: {intelligentValidation.totalDueMatches ? 'VALID' : 'MISMATCH'}
                        </Badge>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-bold">Validation Line Items</h3>
                      <p className="text-sm text-muted-foreground">Line items that add up to New Charges</p>
                    </div>
                  </div>

                  {intelligentValidation.validationLineItems.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-12">No validation line items found</p>
                  ) : (
                    <div className="overflow-x-auto border border-border">
                      <table className="w-full border-collapse" style={{ minWidth: '600px' }}>
                        <thead className="bg-muted">
                          <tr>
                            <th className="border border-border p-2 text-left text-xs font-medium whitespace-nowrap" style={{ minWidth: '50px' }}>Page</th>
                            <th className="border border-border p-2 text-left text-xs font-medium whitespace-nowrap" style={{ minWidth: '300px' }}>Line Item Description</th>
                            <th className="border border-border p-2 text-left text-xs font-medium whitespace-nowrap" style={{ minWidth: '80px' }}>Source</th>
                            <th className="border border-border p-2 text-right text-xs font-medium whitespace-nowrap" style={{ minWidth: '100px' }}>Charge</th>
                          </tr>
                        </thead>
                        <tbody>
                          {intelligentValidation.validationLineItems.map((item, index) => (
                            <tr 
                              key={index}
                              className={`transition-colors ${item.page_number ? 'cursor-pointer hover:bg-muted/50' : ''}`}
                              onClick={() => item.page_number && navigateToPage(item.page_number, item.description)}
                              title={item.page_number ? `Click to go to page ${item.page_number}` : ''}
                            >
                              <td className="border border-border p-2 text-center">
                                {item.page_number ? (
                                  <Badge variant="outline" className="rounded-none text-xs font-mono">
                                    p.{item.page_number}
                                  </Badge>
                                ) : (
                                  <span className="text-xs text-muted-foreground">-</span>
                                )}
                              </td>
                              <td className="border border-border p-2 text-sm" data-testid={`val-line-desc-${index}`}>
                                {item.description}
                                {item._outside_new_charges && (
                                  <Badge variant="outline" className="rounded-none text-[10px] ml-2 border-amber-400 text-amber-700" data-testid={`val-line-outside-${index}`}>
                                    outside New Charges
                                  </Badge>
                                )}
                              </td>
                              <td className="border border-border p-2">
                                <Badge variant="secondary" className="rounded-none text-xs">
                                  {item.source}
                                </Badge>
                              </td>
                              <td className="border border-border p-2 text-right font-mono text-sm">
                                ${item.charge.toFixed(2)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>

                      {/* Validation Summary */}
                      <div className="border border-border border-t-0 bg-muted/50 p-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium">{intelligentValidation.balanceBasis ? 'Line Items Total (balance by service):' : 'Line Items Total:'}</span>
                          <span className="text-lg font-bold font-mono" data-testid="val-line-items-total">${intelligentValidation.validationTotal.toFixed(2)}</span>
                        </div>
                        {intelligentValidation.outsideNewChargesTotal !== 0 && (
                          <div className="flex items-center justify-between text-xs text-amber-700" data-testid="val-outside-new-charges">
                            <span>Excluded (billed outside New Charges, e.g. Connection Charge):</span>
                            <span className="font-mono">${intelligentValidation.outsideNewChargesTotal.toFixed(2)}</span>
                          </div>
                        )}
                        {intelligentValidation.balanceBasis ? (
                          <div className="flex items-center justify-between" data-testid="val-total-balance">
                            <span className="text-sm font-medium">
                              Total Balance (Current ${intelligentValidation.newCharges.toFixed(2)} + Past Due ${intelligentValidation.balanceForward.toFixed(2)}):
                            </span>
                            <span className="text-lg font-bold font-mono">${(intelligentValidation.newCharges + intelligentValidation.balanceForward).toFixed(2)}</span>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium">New Charges (Statement):</span>
                            <span className="text-lg font-bold font-mono">${intelligentValidation.newCharges.toFixed(2)}</span>
                          </div>
                        )}
                        {!intelligentValidation.validationMatchesNewCharges && (
                          <div className={`flex items-center justify-between p-2 ${intelligentValidation.newChargesDifference > 0 ? 'bg-red-50 text-red-700' : 'bg-yellow-50 text-yellow-700'}`}>
                            <span className="text-sm font-medium">Difference:</span>
                            <span className="font-bold font-mono">
                              {intelligentValidation.newChargesDifference > 0 ? '+' : ''}${intelligentValidation.newChargesDifference.toFixed(2)}
                            </span>
                          </div>
                        )}
                        {intelligentValidation.validationMatchesNewCharges && (
                          <div className="flex items-center gap-2 p-2 bg-green-50 text-green-700">
                            <Check className="h-5 w-5" />
                            <span className="font-medium">{intelligentValidation.balanceBasis ? 'Line items match the total balance (current + past due)!' : 'New Charges validated successfully!'}</span>
                          </div>
                        )}
                      </div>

                      {/* Total Amount Due Validation */}
                      <div className="border border-border border-t-0 p-4 space-y-2">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-sm font-bold">Total Amount Due Validation</h4>
                          {intelligentValidation.totalDueMatches && (
                            <Badge variant="outline" className="rounded-none text-xs">
                              Method: {intelligentValidation.totalDueValidationMethod === 'standard' ? 'Standard' :
                                       intelligentValidation.totalDueValidationMethod === 'direct_match' ? 'Direct Match' :
                                       intelligentValidation.totalDueValidationMethod === 'line_items_plus_balance' ? 'Line Items + BF' :
                                       intelligentValidation.totalDueValidationMethod === 'new_charges_plus_bf' ? 'NC + BF' : 'N/A'}
                            </Badge>
                          )}
                        </div>
                        
                        {/* Show breakdown based on which method matched */}
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <span className="text-muted-foreground">New Charges:</span>
                          <span className="font-mono text-right">${intelligentValidation.newCharges.toFixed(2)}</span>
                          
                          {intelligentValidation.breakdown.balanceForward !== 0 && (
                            <>
                              <span className="text-muted-foreground">+ Balance Forward:</span>
                              <span className="font-mono text-right">${intelligentValidation.breakdown.balanceForward.toFixed(2)}</span>
                            </>
                          )}
                          
                          {intelligentValidation.breakdown.lateFee > 0 && !intelligentValidation.breakdown.lateFeeInOtherCharges && (
                            <>
                              <span className="text-muted-foreground">+ Late Fee:</span>
                              <span className="font-mono text-right">${intelligentValidation.breakdown.lateFee.toFixed(2)}</span>
                            </>
                          )}
                          
                          {intelligentValidation.breakdown.lateFee > 0 && intelligentValidation.breakdown.lateFeeInOtherCharges && (
                            <>
                              <span className="text-muted-foreground text-xs italic">Late Fee (in Other Charges):</span>
                              <span className="font-mono text-right text-xs italic">${intelligentValidation.breakdown.lateFee.toFixed(2)}</span>
                            </>
                          )}
                          
                          {intelligentValidation.breakdown.credits > 0 && (
                            <>
                              <span className="text-muted-foreground">- Credits:</span>
                              <span className="font-mono text-right">${intelligentValidation.breakdown.credits.toFixed(2)}</span>
                            </>
                          )}
                          
                          {intelligentValidation.breakdown.adjustments !== 0 && (
                            <>
                              <span className="text-muted-foreground">+ Adjustments:</span>
                              <span className="font-mono text-right">${intelligentValidation.breakdown.adjustments.toFixed(2)}</span>
                            </>
                          )}
                          
                          <span className="font-medium border-t pt-1">= Calculated:</span>
                          <span className="font-mono font-medium text-right border-t pt-1">
                            ${intelligentValidation.calculatedTotalDue.toFixed(2)}
                          </span>
                          <span className="font-medium">Statement Total Due:</span>
                          <span className="font-mono font-medium text-right">${intelligentValidation.totalAmountDue.toFixed(2)}</span>
                        </div>
                        
                        {intelligentValidation.totalDueMatches ? (
                          <div className="flex items-center gap-2 p-2 bg-green-50 text-green-700 mt-2">
                            <Check className="h-5 w-5" />
                            <span className="font-medium">Total Amount Due validated!</span>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between p-2 bg-yellow-50 text-yellow-700 mt-2">
                            <span className="text-sm font-medium">Difference:</span>
                            <span className="font-bold font-mono">
                              {intelligentValidation.totalDueDifference > 0 ? '+' : ''}${intelligentValidation.totalDueDifference.toFixed(2)}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  </>
                </TabsContent>
            </Tabs>
          </div>
        </ResizablePanel>
      </ResizablePanelGroup>

      {/* Audit Trail Modal */}
      <Dialog open={showAuditModal} onOpenChange={setShowAuditModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              Document History
            </DialogTitle>
            <DialogDescription>
              Complete audit trail for this document
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-auto">
            {auditLogs.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">No history available</p>
            ) : (
              <div className="space-y-3">
                {auditLogs.map((log, index) => {
                  const logDate = log.created_at || log.timestamp;
                  const formattedDate = logDate ? format(new Date(logDate), 'MMM d, yyyy h:mm a') : 'Unknown date';
                  return (
                    <div key={log.id || index} className="border border-border rounded-none p-3 bg-card">
                      <div className="flex items-center justify-between mb-2">
                        <Badge variant="outline" className="rounded-none text-xs font-mono">
                          {log.action?.replace(/_/g, ' ')}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {formattedDate}
                        </span>
                      </div>
                      <p className="text-sm">
                        <span className="font-medium">{log.user_name}</span>
                        {log.action === 'status_changed' && log.changes && (
                          <span> changed status from <Badge variant="secondary" className="rounded-none text-xs mx-1">{log.changes.from}</Badge> to <Badge variant="secondary" className="rounded-none text-xs mx-1">{log.changes.to}</Badge></span>
                        )}
                        {log.action === 'assignment_changed' && log.changes && (
                          <span> {log.changes.assigned_to ? `assigned document to ${log.changes.assigned_to_name || 'a user'}` : 'removed assignment'}</span>
                        )}
                        {log.action === 'data_updated' && (
                          <span> updated extracted data</span>
                        )}
                        {log.action === 'reprocessed' && (
                          <span> triggered document re-parsing</span>
                        )}
                        {log.action === 'uploaded' && (
                          <span> uploaded this document</span>
                        )}
                      </p>
                      {log.changes && typeof log.changes === 'object' && !['from', 'to', 'assigned_to'].some(k => k in log.changes) && (
                        <details className="mt-2">
                          <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">
                            View changes ({Object.keys(log.changes).length} fields)
                          </summary>
                          <div className="mt-2 text-xs font-mono bg-muted p-2 rounded-none max-h-32 overflow-auto">
                            {Object.entries(log.changes).map(([field, values]) => (
                              <div key={field} className="mb-1">
                                <span className="text-muted-foreground">{field}:</span>{' '}
                                {typeof values === 'object' && values !== null ? (
                                  <><span className="line-through text-red-600">{String(values.old || 'empty')}</span>{' → '}<span className="text-green-600">{String(values.new || 'empty')}</span></>
                                ) : (
                                  <span className="text-green-600">{String(values)}</span>
                                )}
                              </div>
                            ))}
                          </div>
                        </details>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Viewer;
